import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import API_BASE_URL from "../config/apiConfig";
import "../CurrentAffairs/currentAffairs.css";
import { apiFetch, readJsonSafe } from "../config/apiClient";
import { getUser, isAuthenticated } from "../config/auth";
import Toast from "../Toast/Toast";
import {
  getQuizAttemptSummary,
  isQuizAttempted,
  makeTopicQuizAttemptKey,
  markQuizAttempted,
} from "../config/quizAttemptCache";

const OG_IMAGE_URL = "https://www.aamschool.in/logo512.png";
const SITE_URL = "https://www.aamschool.in";
const TEST_SERIES_TOPIC_LABEL = "Test Series";
const TEST_SERIES_FORMATS = [
  { key: "FULL_TEST", label: "Full Tests", shortLabel: "Full" },
  { key: "SECTIONAL_TEST", label: "Sectional Tests", shortLabel: "Sectional" },
  { key: "PYQ_TEST", label: "PYQ Tests", shortLabel: "PYQ" },
];

const encodePath = (value) => encodeURIComponent(String(value || "").trim());
const decodePath = (value) => decodeURIComponent(String(value || ""));
const buildCanonicalUrl = (path) => `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
const normalizeApiCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.attempts)) return payload.attempts;
  if (Array.isArray(payload?.entries)) return payload.entries;
  return [];
};
const isTestSeriesTopic = (topicName) =>
  String(topicName || "").trim().toLowerCase() === TEST_SERIES_TOPIC_LABEL.toLowerCase();
const readQuizType = (quiz) =>
  String(quiz?.quizType || quiz?.testType || quiz?.format || "")
    .trim()
    .toUpperCase();
const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
const makeSlugWithId = (title, id) => {
  const base = slugify(title) || "quiz";
  return id ? `${base}-${id}` : base;
};

const normalizeLanguage = (lang) => (lang === "hi" ? "hi" : "en");
const translationCache = new Map();
const HINDI_QUIZ_CACHE_PREFIX = "quiz_zone_hi_v5::";
const fullyQuotedTextPattern = /^(["'])[\s\S]*\1$/;
const protectedHindiTextTerms = new Map([
  ["agra", "\u0906\u0917\u0930\u093e"],
  ["kanpur", "\u0915\u093e\u0928\u092a\u0941\u0930"],
  ["lucknow", "\u0932\u0916\u0928\u090a"],
  ["varanasi", "\u0935\u093e\u0930\u093e\u0923\u0938\u0940"],
  ["uttar pradesh", "\u0909\u0924\u094d\u0924\u0930 \u092a\u094d\u0930\u0926\u0947\u0936"],
  ["upsc", "\u092f\u0942\u092a\u0940\u090f\u0938\u0938\u0940"],
  ["ssc", "\u090f\u0938\u090f\u0938\u0938\u0940"]
]);

const cleanupTranslatedText = (text) =>
  String(text || "")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

const hasHindiScript = (text) => /[\u0900-\u097F]/.test(String(text || ""));
const formatDuration = (value) => {
  const totalSeconds = Math.max(0, Number(value) || 0);
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const sentenceCase = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const buildQuizTopicStructuredData = ({
  subjectName,
  topicName,
  canonicalUrl,
  pageTitle,
  pageDescription,
  faq = [],
}) => ({
  collectionPageSchema: {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: pageTitle,
    description: pageDescription,
    url: canonicalUrl,
  },
  breadcrumbSchema: {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${SITE_URL}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Quiz Zone",
        item: `${SITE_URL}/quiz-zone`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: sentenceCase(subjectName) || "Subject",
        item: canonicalUrl,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: sentenceCase(topicName) || "Topic",
        item: canonicalUrl,
      },
    ],
  },
  faqSchema:
    faq.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faq.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        }
      : null,
});

const buildTopicSupportContent = (subjectName, topicName, quizCount = 0) => {
  const safeSubject = sentenceCase(subjectName) || "This subject";
  const safeTopic = sentenceCase(topicName) || "this topic";
  const availableCount = Math.max(0, Number(quizCount) || 0);
  const quizCountLabel =
    availableCount > 0
      ? `${availableCount} practice ${availableCount === 1 ? "quiz" : "quizzes"}`
      : "multiple practice quizzes";

  return {
    overview: `${safeTopic} is an important part of ${safeSubject}. This page is designed to help learners move beyond one-line answers by using ${quizCountLabel} to revise concepts, spot recurring question patterns, and build exam confidence through repeated practice.`,
    whyThisMatters: `${safeTopic} questions often test both memory and understanding. Regular practice helps students recall facts faster, avoid common mistakes, and improve speed during school exams, competitive tests, and self-study revision sessions.`,
    skills: [
      `Strengthen your command over the core ideas, terms, and patterns used in ${safeTopic}.`,
      `Check how well you can apply ${safeSubject} concepts under timed quiz conditions.`,
      "Use the result screen and explanations to find weak areas before the next attempt.",
    ],
    studyPlan: [
      `Start with one quiz and answer every question without guessing too quickly.`,
      "Review the explanations carefully, especially the questions you marked wrong or skipped.",
      "Reattempt the quiz after revision to improve accuracy, speed, and retention.",
    ],
    trustSignals: [
      "Topic-wise practice instead of random mixed questions",
      "Clear score summary with accuracy and time tracking",
      "Question-by-question solution review after submission",
    ],
    faq: [
      {
        question: `Who should use these ${safeTopic} quizzes?`,
        answer: `These quizzes are useful for school students, competitive exam aspirants, and independent learners who want a focused way to revise ${safeTopic} inside ${safeSubject}.`,
      },
      {
        question: `How often should I practice ${safeTopic}?`,
        answer: `A short daily or alternate-day revision cycle works well. Attempt a quiz, review the explanation, and revisit the topic after some time to improve long-term recall.`,
      },
      {
        question: "What should I do after checking my score?",
        answer: "Look at the wrong and unattempted questions first, note the concept behind each mistake, and then retake the quiz to confirm the improvement.",
      },
    ],
  };
};

const LEADERBOARD_FALLBACK_NAMES = [
  "Aarav Sharma",
  "Ananya Verma",
  "Vivaan Singh",
  "Ishita Patel",
  "Aditya Mishra",
  "Diya Nair",
  "Krish Mehta",
  "Sneha Reddy",
  "Rohan Gupta",
  "Priya Yadav",
  "Arjun Malhotra",
  "Kavya Iyer",
  "Rahul Choudhary",
  "Meera Joshi",
  "Siddharth Rao",
  "Neha Kulkarni",
  "Harsh Vardhan",
  "Pooja Sinha",
  "Manav Bansal",
  "Tanvi Kapoor",
  "Aditi Rao",
  "Kunal Saxena",
  "Nisha Bhatia",
  "Dev Khanna",
  "Ritika Sen",
  "Yash Tiwari",
  "Simran Gill",
  "Abhishek Jain",
  "Naina Kapoor",
  "Varun Arora",
  "Shreya Dutta",
  "Akash Thakur",
  "Manya Arora",
  "Nikhil Saini",
  "Ira Banerjee",
  "Parth Desai",
  "Riya Chawla",
  "Tushar Anand",
  "Sana Sheikh",
  "Lakshya Arvind",
  "Vidhi Agarwal",
  "Naman Grover",
  "Aisha Khan",
  "Dhruv Bedi",
  "Sakshi Tandon",
  "Mohit Ahuja",
  "Palak Jindal",
  "Ayush Raj",
  "Muskan Vohra",
  "Rajat Kaushik",
];

const GENERIC_LEADERBOARD_NAMES = new Set(["user", "candidate", "name", "student"]);
const LEADERBOARD_SESSION_ASSIGNMENTS_KEY = "quiz_leaderboard_dummy_assignments_v1";
const LEADERBOARD_SESSION_CURSOR_KEY = "quiz_leaderboard_dummy_cursor_v1";
const LEADERBOARD_DUMMY_BATCH_SIZE = 10;

const resolveLeaderboardEntryName = (entry) =>
  String(
    entry?.user?.username ??
      entry?.user?.name ??
      entry?.username ??
      entry?.name ??
      ""
  ).trim();

const isUsableLeaderboardName = (name) => {
  const normalized = String(name || "").trim();
  if (!normalized) return false;
  return !GENERIC_LEADERBOARD_NAMES.has(normalized.toLowerCase());
};

const getInitials = (name) =>
  String(name || "User")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "U";

const shuffleList = (items) => {
  const nextItems = [...items];
  for (let index = nextItems.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [nextItems[index], nextItems[swapIndex]] = [nextItems[swapIndex], nextItems[index]];
  }
  return nextItems;
};

const rotateList = (items, offset = 0) => {
  if (!items.length) return [];
  const normalizedOffset = ((offset % items.length) + items.length) % items.length;
  return [...items.slice(normalizedOffset), ...items.slice(0, normalizedOffset)];
};

const hashStringToNumber = (value) => {
  let hash = 0;
  const input = String(value || "");
  for (let index = 0; index < input.length; index += 1) {
    hash = (hash * 31 + input.charCodeAt(index)) >>> 0;
  }
  return hash;
};

const readLeaderboardSessionAssignments = () => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(LEADERBOARD_SESSION_ASSIGNMENTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeLeaderboardSessionAssignments = (value) => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(LEADERBOARD_SESSION_ASSIGNMENTS_KEY, JSON.stringify(value));
  } catch {
    // Ignore session storage write failures.
  }
};

const getLeaderboardSessionPool = (sessionKey) => {
  const poolSize = LEADERBOARD_FALLBACK_NAMES.length;
  if (!poolSize) return [];

  if (!sessionKey || typeof window === "undefined") {
    return shuffleList(LEADERBOARD_FALLBACK_NAMES);
  }

  const assignments = readLeaderboardSessionAssignments();
  const existingAssignment = assignments[sessionKey];
  let startIndex = Number(existingAssignment?.startIndex);

  if (!Number.isInteger(startIndex) || startIndex < 0) {
    const storedCursor = Number(window.sessionStorage.getItem(LEADERBOARD_SESSION_CURSOR_KEY));
    const normalizedCursor =
      Number.isInteger(storedCursor) && storedCursor >= 0 ? storedCursor % poolSize : 0;
    startIndex = normalizedCursor;
    assignments[sessionKey] = { startIndex };
    writeLeaderboardSessionAssignments(assignments);
    try {
      window.sessionStorage.setItem(
        LEADERBOARD_SESSION_CURSOR_KEY,
        String((normalizedCursor + LEADERBOARD_DUMMY_BATCH_SIZE) % poolSize)
      );
    } catch {
      // Ignore session storage write failures.
    }
  }

  const rotatedPool = rotateList(LEADERBOARD_FALLBACK_NAMES, startIndex);
  const batch = rotatedPool.slice(0, LEADERBOARD_DUMMY_BATCH_SIZE);
  const remaining = rotatedPool.slice(LEADERBOARD_DUMMY_BATCH_SIZE);
  const orderOffset = batch.length ? hashStringToNumber(sessionKey) % batch.length : 0;
  return [...rotateList(batch, orderOffset), ...remaining];
};

const buildLeaderboardDisplayEntries = (entries, totalMarks, sessionKey = "") => {
  const safeEntries = Array.isArray(entries) ? entries : [];
  const marks = Math.max(1, Number(totalMarks) || 100);
  const usedNames = new Set();
  const fallbackNames = getLeaderboardSessionPool(sessionKey);
  let fallbackCursor = 0;
  let generatedCandidateCount = 0;

  const takeNextLeaderboardName = () => {
    while (fallbackCursor < fallbackNames.length) {
      const nextName = fallbackNames[fallbackCursor];
      fallbackCursor += 1;
      if (!usedNames.has(nextName.toLowerCase())) {
        usedNames.add(nextName.toLowerCase());
        return nextName;
      }
    }

    generatedCandidateCount += 1;
    const fallbackName = `Candidate ${safeEntries.length + generatedCandidateCount}`;
    usedNames.add(fallbackName.toLowerCase());
    return fallbackName;
  };

  const displayEntries = safeEntries.map((entry, index) => {
    const preferredName = resolveLeaderboardEntryName(entry);
    const normalizedPreferredName = preferredName.toLowerCase();
    const name =
      isUsableLeaderboardName(preferredName) && !usedNames.has(normalizedPreferredName)
        ? preferredName
        : takeNextLeaderboardName();

    if (name === preferredName) {
      usedNames.add(normalizedPreferredName);
    }

    return {
      rank: Number(entry?.rank) || index + 1,
      name,
      score: Math.max(0, Number(entry?.score) || 0),
      totalMarks: Math.max(1, Number(entry?.totalMarks) || marks),
      timeTakenSeconds: Math.max(1, Number(entry?.timeTakenSeconds) || 1),
      isCurrentUser: Boolean(entry?.isCurrentUser),
      isSample: false,
    };
  });

  const sortedEntries = [...displayEntries]
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;
      return left.timeTakenSeconds - right.timeTakenSeconds;
    })
    .slice(0, 10)
    .map((entry, index) => ({
      ...entry,
      rank: index + 1,
    }));

  if (displayEntries.length >= 10) {
    const currentUserIndex = sortedEntries.findIndex((entry) => entry.isCurrentUser);
    if (currentUserIndex <= 0) {
      return sortedEntries;
    }

    const [currentUserEntry] = sortedEntries.splice(currentUserIndex, 1);
    return [currentUserEntry, ...sortedEntries];
  }

  for (let index = displayEntries.length; index < 10; index += 1) {
    const fallbackName = takeNextLeaderboardName();
    const minScore = Math.max(1, Math.ceil(marks * 0.68));
    const maxScore = Math.max(minScore, Math.min(marks, Math.ceil(marks * 0.97)));
    const score = Math.floor(Math.random() * (maxScore - minScore + 1)) + minScore;
    const timeTakenSeconds = 70 + Math.floor(Math.random() * 220);
    displayEntries.push({
      rank: index + 1,
      name: fallbackName,
      score,
      totalMarks: marks,
      timeTakenSeconds,
      isCurrentUser: false,
      isSample: true,
    });
  }

  const completedEntries = [...displayEntries]
      .sort((left, right) => {
        if (right.score !== left.score) return right.score - left.score;
        return left.timeTakenSeconds - right.timeTakenSeconds;
      })
      .slice(0, 10)
      .map((entry, index) => ({
        ...entry,
        rank: index + 1,
      }));
  const currentUserIndex = completedEntries.findIndex((entry) => entry.isCurrentUser);
  if (currentUserIndex <= 0) {
    return completedEntries;
  }

  const [currentUserEntry] = completedEntries.splice(currentUserIndex, 1);
  return [currentUserEntry, ...completedEntries];
};

const getExplanationPayload = (question) => {
  const explanation = question?.explanation;
  return {
    text:
      question?.details_description ||
      explanation?.text ||
      question?.explanationText ||
      "",
    diagramType:
      question?.explanation_diagram_type ||
      explanation?.diagramType ||
      null,
    diagramData:
      question?.explanation_diagram_data ||
      explanation?.diagramData ||
      null,
  };
};

const looksLikeAsciiDiagram = (block) => {
  const lines = String(block || "")
    .split("\n")
    .map((line) => line.trimEnd())
    .filter(Boolean);

  if (lines.length < 2) return false;

  return lines.some((line) => /[<>^|_\\/`~\-[\]{}()+=*]|[↑↓←→]/.test(line));
};

const normalizeDiagramSegment = (segment) => {
  const x1 = Number(segment?.x1) || 0;
  const y1 = Number(segment?.y1) || 0;
  let x2 = Number(segment?.x2);
  let y2 = Number(segment?.y2);
  const hasTarget = Number.isFinite(x2) && Number.isFinite(y2);
  const declaredDistance = Number(segment?.distance);
  const distanceUnits = declaredDistance > 0 ? declaredDistance : 1;
  const minVisualLength = 20;
  const visualLength = Math.max(distanceUnits * 12, minVisualLength);
  const direction = String(segment?.direction || "")
    .trim()
    .toUpperCase();

  if (!hasTarget || (Math.abs(x2 - x1) < 0.5 && Math.abs(y2 - y1) < 0.5)) {
    switch (direction) {
      case "N":
        x2 = x1;
        y2 = y1 - visualLength;
        break;
      case "S":
        x2 = x1;
        y2 = y1 + visualLength;
        break;
      case "E":
        x2 = x1 + visualLength;
        y2 = y1;
        break;
      case "W":
        x2 = x1 - visualLength;
        y2 = y1;
        break;
      case "NE":
        x2 = x1 + visualLength * 0.7;
        y2 = y1 - visualLength * 0.7;
        break;
      case "NW":
        x2 = x1 - visualLength * 0.7;
        y2 = y1 - visualLength * 0.7;
        break;
      case "SE":
        x2 = x1 + visualLength * 0.7;
        y2 = y1 + visualLength * 0.7;
        break;
      case "SW":
        x2 = x1 - visualLength * 0.7;
        y2 = y1 + visualLength * 0.7;
        break;
      default:
        x2 = x1 + visualLength;
        y2 = y1;
        break;
    }
  }

  return {
    ...segment,
    x1,
    y1,
    x2,
    y2,
  };
};

const buildFallbackDirectionSegment = (data) => {
  const startPoint = data?.startPoint || (Array.isArray(data?.points) ? data.points[0] : null);
  const finalDirection = String(data?.finalDirection || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "")
    .replace("उत्तर", "N")
    .replace("दक्षिण", "S")
    .replace("पूर्व", "E")
    .replace("पश्चिम", "W")
    .replace(/-/g, "");

  if (!startPoint || !finalDirection) return null;

  return normalizeDiagramSegment({
    x1: Number(startPoint?.x) || 0,
    y1: Number(startPoint?.y) || 0,
    x2: Number(startPoint?.x) || 0,
    y2: Number(startPoint?.y) || 0,
    direction: finalDirection,
    distance: 1,
    label: data?.finalDirection || "",
  });
};

function DirectionSenseSvgDiagram({ data }) {
  const canvas = data?.canvas || {};
  const width = Math.max(160, Number(canvas?.width) || 320);
  const height = Math.max(140, Number(canvas?.height) || 220);
  const rawSegments = Array.isArray(data?.segments) ? data.segments.map(normalizeDiagramSegment) : [];
  const fallbackSegment = rawSegments.length ? null : buildFallbackDirectionSegment(data);
  const segments = fallbackSegment ? [fallbackSegment] : rawSegments;
  const points = Array.isArray(data?.points) ? data.points : [];
  const compass = data?.compass || null;

  if (!segments.length && !points.length) return null;

  return (
    <div className="quiz-explanation-diagram-shell">
      <svg
        className="quiz-explanation-diagram"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Direction sense explanation diagram"
      >
        <defs>
          <marker
            id="quiz-direction-arrow"
            markerWidth="6"
            markerHeight="6"
            refX="5"
            refY="3"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M 0 0 L 6 3 L 0 6 z" fill="#0d47a1" />
          </marker>
        </defs>

        <rect x="0" y="0" width={width} height={height} rx="18" fill="#f8fbff" />

        {compass?.show ? (
          <g className="quiz-diagram-compass" transform={`translate(${compass?.x || width - 50} ${compass?.y || 32})`}>
            <circle r="18" fill="#fff" stroke="#b9cbe7" strokeWidth="1.5" />
            <line x1="0" y1="-12" x2="0" y2="12" stroke="#89a3c8" strokeWidth="1.5" />
            <line x1="-12" y1="0" x2="12" y2="0" stroke="#89a3c8" strokeWidth="1.5" />
            <text x="0" y="-18" textAnchor="middle">N</text>
            <text x="0" y="30" textAnchor="middle">S</text>
            <text x="22" y="5" textAnchor="middle">E</text>
            <text x="-22" y="5" textAnchor="middle">W</text>
          </g>
        ) : null}

        {segments.map((segment, index) => {
          const midX = (Number(segment?.x1) + Number(segment?.x2)) / 2;
          const midY = (Number(segment?.y1) + Number(segment?.y2)) / 2;
          return (
            <g key={`segment-${index}`}>
              <line
                x1={segment?.x1}
                y1={segment?.y1}
                x2={segment?.x2}
                y2={segment?.y2}
                stroke="#0d47a1"
                strokeWidth="2.5"
                strokeLinecap="round"
                markerEnd="url(#quiz-direction-arrow)"
              />
              {segment?.label ? (
                <text x={midX} y={midY - 8} textAnchor="middle" className="quiz-diagram-distance-label">
                  {segment.label}
                </text>
              ) : null}
            </g>
          );
        })}

        {points.map((point, index) => (
          <g key={`point-${index}`}>
            <circle
              cx={point?.x}
              cy={point?.y}
              r="3.5"
              fill={index === points.length - 1 ? "#2eaf53" : "#c62828"}
            />
            {point?.label ? (
              <text x={(Number(point?.x) || 0) + 10} y={(Number(point?.y) || 0) - 10} className="quiz-diagram-point-label">
                {point.label}
              </text>
            ) : null}
          </g>
        ))}
      </svg>
      {data?.finalDirection || data?.finalAnswerLabel ? (
        <div className="quiz-explanation-diagram-meta">
          {data?.finalDirection ? <span>{`Final Direction: ${data.finalDirection}`}</span> : null}
          {data?.finalAnswerLabel ? <span>{`Answer: ${data.finalAnswerLabel}`}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

function ExplanationContent({ text, diagramType, diagramData }) {
  const blocks = String(text || "")
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);
  const shouldRenderSvg = diagramType === "DIRECTION_SENSE_SVG" && diagramData;

  if (!blocks.length && !shouldRenderSvg) {
    return <>No explanation available.</>;
  }

  return (
    <>
      {blocks.map((block, index) =>
        looksLikeAsciiDiagram(block) ? (
          <pre key={`exp-block-${index}`} className="quiz-explanation-ascii">
            {block}
          </pre>
        ) : (
          <p key={`exp-block-${index}`} className="quiz-explanation-paragraph">
            {block}
          </p>
        )
      )}
      {shouldRenderSvg ? <DirectionSenseSvgDiagram data={diagramData} /> : null}
    </>
  );
}

const formatSubmittedAt = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const clampPercent = (value) => Math.max(0, Math.min(100, Number(value) || 0));
const areOptionsEqual = (left, right) =>
  String(left || "").trim().toLowerCase() === String(right || "").trim().toLowerCase();
const normalizeDifficultyLabel = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized.includes("easy")) return "easy";
  if (normalized.includes("hard")) return "hard";
  return "medium";
};
const QUIZ_SOLUTION_CACHE_PREFIX = "quiz_solution_v1::";

const makeSolutionCacheKey = (attemptKey) =>
  `${QUIZ_SOLUTION_CACHE_PREFIX}${String(attemptKey || "").trim().toLowerCase()}`;

const saveAttemptSolutions = (attemptKey, payload) => {
  if (!attemptKey || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(makeSolutionCacheKey(attemptKey), JSON.stringify(payload));
  } catch {
    // Ignore storage write failures.
  }
};

const readAttemptSolutions = (attemptKey) => {
  if (!attemptKey || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(makeSolutionCacheKey(attemptKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const buildAttemptSummary = ({
  responseData,
  normalizedQuestions,
  userAnswers,
  timeTakenSeconds,
  currentUserId,
}) => {
  const selectedAnswers = Object.values(userAnswers || {}).filter(
    (value) => value && typeof value === "object"
  );
  const attemptedQuestions = Number(responseData?.attemptedQuestions ?? selectedAnswers.length) || 0;
  const correctAnswers =
    Number(responseData?.correctAnswers) ||
    normalizedQuestions.reduce((count, question, index) => {
      const selectedAnswer = userAnswers?.[index];
      if (!selectedAnswer) return count;
      const choiceMatched =
        selectedAnswer?.choiceId != null &&
        question?.correct_choice_id != null &&
        selectedAnswer.choiceId === question.correct_choice_id;
      const textMatched =
        selectedAnswer?.text &&
        question?.correct_option &&
        selectedAnswer.text === question.correct_option;
      return choiceMatched || textMatched ? count + 1 : count;
    }, 0);
  const wrongAnswers = Math.max(
    0,
    Number(responseData?.wrongAnswers ?? attemptedQuestions - correctAnswers) || 0
  );
  const totalMarks = Math.max(
    correctAnswers,
    Number(responseData?.totalMarks ?? normalizedQuestions.length) || 0
  );
  const score = Math.max(0, Number(responseData?.score ?? correctAnswers) || 0);
  const accuracy =
    responseData?.accuracy != null
      ? clampPercent(responseData.accuracy)
      : attemptedQuestions
      ? clampPercent((correctAnswers / attemptedQuestions) * 100)
      : 0;

  return {
    userId: currentUserId ?? null,
    score,
    totalMarks,
    accuracy,
    correctAnswers,
    wrongAnswers,
    attemptedQuestions,
    timeTakenSeconds: Math.max(1, Number(responseData?.timeTakenSeconds ?? timeTakenSeconds) || 1),
    attemptNumber: Number(responseData?.attemptNumber) || null,
    submittedAt: responseData?.submittedAt || new Date().toISOString(),
  };
};

const buildAttemptSummaryFromAttempt = (attempt, currentUserId) => {
  if (!attempt || typeof attempt !== "object") return null;

  return {
    userId: currentUserId ?? attempt?.user?.userId ?? null,
    score: Math.max(0, Number(attempt?.score) || 0),
    totalMarks: Math.max(0, Number(attempt?.totalMarks) || 0),
    accuracy: clampPercent(
      attempt?.accuracy ??
        ((Number(attempt?.attemptedQuestions) || 0) > 0
          ? ((Number(attempt?.correctAnswers) || 0) / Number(attempt?.attemptedQuestions)) * 100
          : 0)
    ),
    correctAnswers: Math.max(0, Number(attempt?.correctAnswers) || 0),
    wrongAnswers: Math.max(0, Number(attempt?.wrongAnswers) || 0),
    attemptedQuestions: Math.max(0, Number(attempt?.attemptedQuestions) || 0),
    timeTakenSeconds: Math.max(1, Number(attempt?.timeTakenSeconds) || 1),
    attemptNumber: Number(attempt?.attemptNumber) || null,
    submittedAt: attempt?.submittedAt || attempt?.createdAt || null,
  };
};

const redirectToLoginForQuiz = (navigate, redirectTo) => {
  navigate("/login", {
    replace: true,
    state: {
      loginMessage: "Please log in to open and attempt this quiz.",
      redirectTo,
    },
  });
};

function QuizBackButton({ onClick, label = "Back" }) {
  return (
    <button type="button" className="quiz-back-btn" onClick={onClick}>
      <span aria-hidden="true">←</span>
      <span>{label}</span>
    </button>
  );
}

const escapeRegExp = (value) =>
  String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const makePlaceholderToken = (prefix, index) =>
  `${String.fromCharCode(0xe000)}${prefix}${index}${String.fromCharCode(0xe001)}`;

const replacePlaceholderVariants = (text, { token, index }, replacement, prefix) => {
  let restored = String(text || "");
  const safeReplacement = replacement || "";
  const exactPattern = new RegExp(escapeRegExp(token), "g");
  const legacyPatterns = [
    new RegExp(`AAMSCHOOL${prefix}${index}TOKEN`, "gi"),
    new RegExp(`AAMSCHOOL${prefix}${index}टोकन`, "gi"),
  ];

  if (prefix === "KEEP") {
    legacyPatterns.push(new RegExp(`आमस्कूलकीप${index}टोकन`, "g"));
  }

  if (prefix === "TERM") {
    legacyPatterns.push(new RegExp(`आमस्कूलटर्म${index}टोकन`, "g"));
  }

  restored = restored.replace(exactPattern, safeReplacement);
  legacyPatterns.forEach((pattern) => {
    restored = restored.replace(pattern, safeReplacement);
  });

  return restored;
};

const normalizeQuestionLanguage = (lang) => {
  const value = String(lang || "").trim().toLowerCase();
  if (value === "hi" || value === "hindi") return "hi";
  return "en";
};

const getQuestionSourceLanguage = (question) =>
  normalizeQuestionLanguage(
    question?.language ??
      question?.laungauge ??
      question?.launguage ??
      question?.laungause
  );

const shouldSkipTranslation = (text, sourceLang) => {
  const value = String(text || "").trim();
  if (!value) return true;
  if (normalizeLanguage(sourceLang) === "hi") {
    return false;
  }
  if (fullyQuotedTextPattern.test(value)) return true;
  if (!/[a-z]/.test(value)) return true;
  return false;
};

const maskProtectedTerms = (text) => {
  let maskedText = String(text || "");
  const replacements = [];

  protectedHindiTextTerms.forEach((replacement, source) => {
    const pattern = new RegExp(`\\b${escapeRegExp(source)}\\b`, "gi");
    maskedText = maskedText.replace(pattern, (matched) => {
      const token = makePlaceholderToken("TERM", replacements.length);
      replacements.push({ token, replacement, source: matched, index: replacements.length });
      return token;
    });
  });

  return { maskedText, replacements };
};

const maskNonTranslatableSegments = (text, sourceLang = "en") => {
  if (normalizeLanguage(sourceLang) === "hi") {
    return { maskedText: String(text || ""), replacements: [] };
  }

  let maskedText = String(text || "");
  const replacements = [];
  const addReplacement = (segment) => {
    const token = makePlaceholderToken("KEEP", replacements.length);
    replacements.push({ token, replacement: segment, index: replacements.length });
    return token;
  };

  maskedText = maskedText.replace(/"[^"\r\n]*"|'[^'\r\n]*'/g, (matched) =>
    addReplacement(matched)
  );
  maskedText = maskedText.replace(/\b[A-Z][A-Z0-9./:+-]*\b|\b\d+(?:[./:+-]\d+)*\b/g, (matched) => {
    return addReplacement(matched);
  });

  return { maskedText, replacements };
};

const forceRestorePlaceholderPrefixes = (text, replacements, prefix) => {
  let restored = String(text || "");
  (replacements || []).forEach(({ replacement, source, index }) => {
    const safeReplacement = replacement || source || "";
    const basePattern = new RegExp(`${prefix}${index}`, "gi");
    const wrappedPattern = new RegExp(
      `${String.fromCharCode(0xe000)}\\s*${prefix}${index}\\s*${String.fromCharCode(0xe001)}`,
      "g"
    );
    const leadingPattern = new RegExp(
      `${String.fromCharCode(0xe000)}\\s*${prefix}${index}`,
      "g"
    );
    const trailingPattern = new RegExp(
      `${prefix}${index}\\s*${String.fromCharCode(0xe001)}`,
      "g"
    );

    restored = restored
      .replace(wrappedPattern, safeReplacement)
      .replace(leadingPattern, safeReplacement)
      .replace(trailingPattern, safeReplacement)
      .replace(basePattern, safeReplacement);
  });
  return restored;
};

const unmaskProtectedTerms = (text, replacements) => {
  let restored = String(text || "");
  (replacements || []).forEach(({ token, replacement, source, index }) => {
    restored = replacePlaceholderVariants(
      restored,
      { token, index },
      replacement || source || "",
      "TERM"
    );
  });
  return forceRestorePlaceholderPrefixes(restored, replacements, "TERM");
};

const unmaskNonTranslatableSegments = (text, replacements) => {
  let restored = String(text || "");
  (replacements || []).forEach(({ token, replacement, index }) => {
    restored = replacePlaceholderVariants(restored, { token, index }, replacement || "", "KEEP");
  });
  return forceRestorePlaceholderPrefixes(restored, replacements, "KEEP");
};

const hasStrongHindiSignal = (text) => {
  const value = String(text || "");
  const devanagariCount = (value.match(/[\u0900-\u097F]/g) || []).length;
  const letterCount = (value.match(/[A-Za-z\u0900-\u097F]/g) || []).length;
  if (!letterCount) return false;
  return devanagariCount >= Math.max(2, Math.floor(letterCount * 0.2));
};

const isOptionTranslationInvalid = (source, translated) => {
  const src = String(source || "").trim().toLowerCase();
  const tgt = String(translated || "").trim().toLowerCase();
  if (!src || !tgt) return true;
  if ((tgt.includes("\u092e\u0948\u0902") || tgt.includes("main ")) && src.split(/\s+/).length <= 4) {
    return true;
  }
  if (/aamschoolterm\d+token/i.test(tgt)) return true;
  if (!hasHindiScript(tgt) && tgt !== src) return true;
  return false;
};

const isQuestionTranslationInvalid = (source, translated) => {
  const src = String(source || "").trim();
  const tgt = String(translated || "").trim();
  if (!src || !tgt) return true;
  if (/aamschoolterm\d+token/i.test(tgt)) return true;
  if (!hasHindiScript(tgt) && tgt.toLowerCase() !== src.toLowerCase()) return true;
  return false;
};

const translateText = async (text, targetLang, sourceLang = "en") => {
  const value = String(text || "").trim();
  const lang = normalizeLanguage(targetLang);
  const source = normalizeLanguage(sourceLang);
  if (lang === source || shouldSkipTranslation(value, source)) return value;

  const directProtectedTerm =
    lang === "hi" && source === "en"
      ? protectedHindiTextTerms.get(value.toLowerCase())
      : null;
  if (directProtectedTerm) return directProtectedTerm;

  const cacheKey = `${source}->${lang}::${value}`;
  if (translationCache.has(cacheKey)) return translationCache.get(cacheKey);

  const { maskedText: protectedMaskedText, replacements: protectedReplacements } =
    lang === "hi" && source === "en"
      ? maskProtectedTerms(value)
      : { maskedText: value, replacements: [] };
  const { maskedText, replacements: preservedReplacements } =
    maskNonTranslatableSegments(protectedMaskedText, source);

  const fetchGoogleTranslation = async (input, target) => {
    const res = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${source}&tl=${target}&dt=t&q=${encodeURIComponent(
        input
      )}`
    );
    if (!res.ok) throw new Error("Google translation failed");
    const data = await res.json();
    const translated = data[0]
      ?.map((item) => (Array.isArray(item) ? item[0] : ""))
      .join("");
    return translated || input;
  };

  const fetchMyMemoryTranslation = async (input, target) => {
    const res = await fetch(
      `https://api.mymemory.translated.net/get?q=${encodeURIComponent(
        input
      )}&langpair=${source}|${target}`
    );
    if (!res.ok) throw new Error("MyMemory translation failed");
    const data = await res.json();
    return data?.responseData?.translatedText || input;
  };

  try {
    let translated = cleanupTranslatedText(await fetchGoogleTranslation(maskedText, lang));
    translated = unmaskProtectedTerms(translated, protectedReplacements);
    translated = unmaskNonTranslatableSegments(translated, preservedReplacements);

    if (
      lang === "hi" &&
      (isQuestionTranslationInvalid(value, translated) || !hasStrongHindiSignal(translated))
    ) {
      translated = cleanupTranslatedText(await fetchMyMemoryTranslation(maskedText, lang));
      translated = unmaskProtectedTerms(translated, protectedReplacements);
      translated = unmaskNonTranslatableSegments(translated, preservedReplacements);
    }

    const finalText =
      lang === "hi" &&
      (isQuestionTranslationInvalid(value, translated) || !hasStrongHindiSignal(translated))
        ? value
        : translated;
    translationCache.set(cacheKey, finalText);
    return finalText;
  } catch {
    try {
      const fallback = cleanupTranslatedText(await fetchMyMemoryTranslation(maskedText, lang));
      const restored = unmaskNonTranslatableSegments(
        unmaskProtectedTerms(fallback, protectedReplacements),
        preservedReplacements
      );
      const finalText =
        lang === "hi" &&
        (isQuestionTranslationInvalid(value, restored) || !hasStrongHindiSignal(restored))
          ? value
          : restored;
      translationCache.set(cacheKey, finalText);
      return finalText;
    } catch {
      translationCache.set(cacheKey, value);
      return value;
    }
  }
};

const translateOptionToHindi = async (optionText, targetLang = "hi", sourceLang = "en") => {
  const protectedTerm =
    normalizeLanguage(targetLang) === "hi" && normalizeLanguage(sourceLang) === "en"
      ? protectedHindiTextTerms.get(String(optionText || "").trim().toLowerCase())
      : null;
  if (protectedTerm) return protectedTerm;
  const translated = await translateText(optionText, targetLang, sourceLang);
  if (normalizeLanguage(targetLang) !== "hi") {
    return translated;
  }
  if (isOptionTranslationInvalid(optionText, translated)) {
    return optionText;
  }
  return translated;
};

const translateQuestionToHindi = async (questionText, targetLang = "hi", sourceLang = "en") => {
  const translated = await translateText(questionText, targetLang, sourceLang);
  if (normalizeLanguage(targetLang) !== "hi") {
    return translated;
  }
  if (isQuestionTranslationInvalid(questionText, translated)) {
    return questionText;
  }
  return translated;
};
const mapWithConcurrency = async (items, limit, mapper) => {
  const results = new Array(items.length);
  let index = 0;

  const worker = async () => {
    while (index < items.length) {
      const currentIndex = index++;
      results[currentIndex] = await mapper(items[currentIndex], currentIndex);
    }
  };

  const workers = Array.from(
    { length: Math.max(1, Math.min(limit, items.length)) },
    () => worker()
  );
  await Promise.all(workers);
  return results;
};

const fetchTopicQuizzes = async (subject, topic, quizType = "") => {
  const encodedSubject = encodeURIComponent(subject);
  const encodedTopic = encodeURIComponent(topic);
  const encodedQuizType = encodeURIComponent(quizType);
  const urls = [
    `${API_BASE_URL}/quizzes/by-topic?subject=${encodedSubject}&topic=${encodedTopic}${
      quizType ? `&quizType=${encodedQuizType}` : ""
    }`,
    `${API_BASE_URL}/quizzes/by-topic?topic=${encodedTopic}${
      quizType ? `&quizType=${encodedQuizType}` : ""
    }`,
    `${API_BASE_URL}/quizzes/by-topic/${encodedTopic}`,
  ];

  let lastError = null;
  for (const url of urls) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        lastError = new Error(`Failed to load quizzes: ${response.status}`);
        continue;
      }
      const data = await response.json();
      return normalizeApiCollection(data);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error("Failed to load quizzes.");
};

const fetchQuizDetails = async (slug, id) => {
  const urls = [];
  if (slug) {
    const encodedSlug = encodeURIComponent(slug);
    urls.push(`${API_BASE_URL}/quizzes/${encodedSlug}`);
    urls.push(`${API_BASE_URL}/quizzes/slug/${encodedSlug}`);
  }
  if (id) {
    urls.push(`${API_BASE_URL}/quizzes/${id}`);
  }

  let lastError = null;
  for (const url of urls) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        lastError = new Error(`Failed to load quiz: ${response.status}`);
        continue;
      }
      return await response.json();
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error("Failed to load quiz.");
};

const fetchQuizLeaderboard = async (quizId, { auth = false } = {}) => {
  const response = await apiFetch(`/quizzes/${quizId}/leaderboard`, { auth });
  const data = await readJsonSafe(response);

  if (!response.ok) {
    throw new Error(data?.message || "Unable to load leaderboard. Please try again.");
  }

  return data;
};

const fetchMyQuizAttempts = async (quizId) => {
  const response = await apiFetch(`/quizzes/${quizId}/my-attempts`, { auth: true });
  const data = await readJsonSafe(response);

  if (!response.ok) {
    throw new Error(data?.message || "Unable to load your attempts. Please try again.");
  }

  return normalizeApiCollection(data)
    .filter((attempt) => attempt && typeof attempt === "object")
    .sort((left, right) => {
      const rightTime = new Date(right?.submittedAt || right?.createdAt || 0).getTime() || 0;
      const leftTime = new Date(left?.submittedAt || left?.createdAt || 0).getTime() || 0;
      return rightTime - leftTime;
    });
};

const normalizeMcqQuestion = (question) => {
  const raw = question?.question || question || {};
  const quizQuestionId = question?.id ?? raw?.id ?? null;
  const questionLanguage =
    raw?.language ??
    raw?.laungauge ??
    raw?.launguage ??
    raw?.laungause ??
    question?.language ??
    question?.laungauge ??
    question?.launguage ??
    question?.laungause;
  const choices = Array.isArray(raw?.choices)
    ? raw.choices
    : Array.isArray(raw?.options)
    ? raw.options.map((option, idx) =>
        typeof option === "string" ? { id: idx, text: option } : option
      )
    : [];
  const options = choices.map((choice) => choice?.text).filter(Boolean);

  const correctChoiceId = Array.isArray(raw?.correctAnswer?.choiceIds)
    ? raw.correctAnswer.choiceIds[0]
    : null;

  const correctFromChoiceId =
    typeof correctChoiceId === "number"
      ? choices.find((choice) => choice?.id === correctChoiceId)?.text || null
      : null;

  // Backend may send 1-based or 0-based index in choiceIds.
  const correctFromOneBased =
    typeof correctChoiceId === "number" && options[correctChoiceId - 1]
      ? options[correctChoiceId - 1]
      : null;
  const correctFromZeroBased =
    typeof correctChoiceId === "number" && options[correctChoiceId]
      ? options[correctChoiceId]
      : null;

  return {
    id: quizQuestionId,
    quizQuestionId,
    question: raw?.questionText || raw?.question || "",
    language: questionLanguage,
    choices,
    options,
    correct_choice_id: typeof correctChoiceId === "number" ? correctChoiceId : null,
    correct_option:
      raw?.correct_option ||
      correctFromChoiceId ||
      correctFromOneBased ||
      correctFromZeroBased ||
      "",
    details_description:
      raw?.explanation?.text || raw?.explanationText || raw?.details_description || "",
    explanation: raw?.explanation || null,
    explanationText: raw?.explanationText || "",
    explanation_diagram_type: raw?.explanation?.diagramType || null,
    explanation_diagram_data: raw?.explanation?.diagramData || null,
    difficulty: raw?.difficulty || question?.difficulty || "medium",
  };
};

export function TopicQuizListPage() {
  const { subject, topic } = useParams();
  const navigate = useNavigate();
  const subjectName = decodePath(subject);
  const topicName = decodePath(topic);
  const isTestSeries = isTestSeriesTopic(topicName);
  const hubPath = isTestSeries ? "/test-series" : "/quiz-zone";
  const currentUserId = getUser()?.id ?? null;
  const [quizzes, setQuizzes] = useState([]);
  const [attemptSummaries, setAttemptSummaries] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeFormat, setActiveFormat] = useState(TEST_SERIES_FORMATS[0].key);
  const canonicalUrl = buildCanonicalUrl(
    `/quiz-zone/${encodePath(subjectName)}/${encodePath(topicName)}/quizzes`
  );
  const pageTitle = `${topicName} Quiz | ${subjectName} | AAMSchool`;
  const pageDescription = `Practice ${topicName} quiz questions from ${subjectName} on AAMSchool with topic-wise quizzes, score tracking, and revision support.`;
  const topicSupportContent = useMemo(
    () => buildTopicSupportContent(subjectName, topicName, quizzes.length),
    [quizzes.length, subjectName, topicName]
  );
  const quizzesByFormat = useMemo(() => {
    if (!isTestSeries) return {};
    return TEST_SERIES_FORMATS.reduce((acc, format) => {
      acc[format.key] = quizzes.filter((quiz) => readQuizType(quiz) === format.key);
      return acc;
    }, {});
  }, [isTestSeries, quizzes]);
  const visibleQuizzes = isTestSeries ? quizzesByFormat[activeFormat] || [] : quizzes;
  const topicStructuredData = useMemo(
    () =>
      buildQuizTopicStructuredData({
        subjectName,
        topicName,
        canonicalUrl,
        pageTitle,
        pageDescription,
        faq: topicSupportContent.faq,
      }),
    [canonicalUrl, pageDescription, pageTitle, subjectName, topicName, topicSupportContent.faq]
  );

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = isTestSeries
          ? (
              await Promise.all(
                TEST_SERIES_FORMATS.map((format) =>
                  fetchTopicQuizzes(subjectName, topicName, format.key)
                )
              )
            ).flat()
          : await fetchTopicQuizzes(subjectName, topicName);
        if (!mounted) return;
        setQuizzes(data);
      } catch (err) {
        if (!mounted) return;
        setError(err.message || "Failed to load quizzes.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [isTestSeries, subjectName, topicName]);

  useEffect(() => {
    if (!isTestSeries) return;
    if ((quizzesByFormat[activeFormat] || []).length) return;
    const firstAvailable = TEST_SERIES_FORMATS.find((format) => (quizzesByFormat[format.key] || []).length);
    if (firstAvailable && firstAvailable.key !== activeFormat) {
      setActiveFormat(firstAvailable.key);
    }
  }, [activeFormat, isTestSeries, quizzesByFormat]);

  useEffect(() => {
    if (!currentUserId || !quizzes.length) {
      setAttemptSummaries({});
      return;
    }

    let mounted = true;

    const loadAttemptSummaries = async () => {
      const summaries = await Promise.all(
        quizzes.map(async (quiz, idx) => {
          const title = quiz?.title || `Quiz ${idx + 1}`;
          const slug = quiz?.slug || makeSlugWithId(title, quiz?.id ?? idx);
          const id = quiz?.id ?? "";
          const attemptKey = makeTopicQuizAttemptKey({
            subject: subjectName,
            topic: topicName,
            slug,
            id,
          });

          let summary = getQuizAttemptSummary(attemptKey, currentUserId);

          if (id) {
            try {
              const attempts = await fetchMyQuizAttempts(id);
              if (attempts.length) {
                summary = buildAttemptSummaryFromAttempt(attempts[0], currentUserId);
                markQuizAttempted(attemptKey, summary);
              }
            } catch {
              // Ignore leaderboard errors here and fall back to cached summary.
            }
          }

          return [attemptKey, summary];
        })
      );

      if (!mounted) return;
      setAttemptSummaries(
        summaries.reduce((acc, [attemptKey, summary]) => {
          if (summary) {
            acc[attemptKey] = summary;
          }
          return acc;
        }, {})
      );
    };

    loadAttemptSummaries();

    return () => {
      mounted = false;
    };
  }, [currentUserId, quizzes, subjectName, topicName]);

  const handleProtectedQuizNavigation = useCallback(
    (event, targetPath) => {
      if (isAuthenticated()) return;
      event.preventDefault();
      redirectToLoginForQuiz(navigate, targetPath);
    },
    [navigate]
  );

  return (
    <div className="quiz-page">
      <Helmet>
        <title>{pageTitle}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <script type="application/ld+json">
          {JSON.stringify(topicStructuredData.collectionPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(topicStructuredData.breadcrumbSchema)}
        </script>
        {topicStructuredData.faqSchema ? (
          <script type="application/ld+json">{JSON.stringify(topicStructuredData.faqSchema)}</script>
        ) : null}
      </Helmet>
      <QuizBackButton
        onClick={() =>
          isTestSeries
            ? navigate(hubPath)
            : navigate(hubPath, { state: { selectedSubject: subjectName } })
        }
      />
      <h1>{topicName} Quiz</h1>
      <p className="quiz-meta">
        Subject: <strong>{subjectName}</strong>
      </p>
      {isTestSeries ? (
        <div className="quiz-list-card">
          <div className="quiz-report-tabs" role="tablist" aria-label="Test series format tabs">
            {TEST_SERIES_FORMATS.map((format) => (
              <button
                key={format.key}
                type="button"
                role="tab"
                className={`quiz-report-tab ${activeFormat === format.key ? "active" : ""}`}
                aria-selected={activeFormat === format.key}
                onClick={() => setActiveFormat(format.key)}
              >
                {format.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {loading ? <p>Loading quizzes...</p> : null}
      {error ? <p className="quiz-error">{error}</p> : null}

      {!loading && !error && (
        <div className="quiz-list-card">
          {visibleQuizzes.length === 0 ? (
            <p>
              {isTestSeries
                ? `No ${TEST_SERIES_FORMATS.find((format) => format.key === activeFormat)?.shortLabel || ""} tests available right now.`
                : "No quizzes available for this topic."}
            </p>
          ) : null}
          {visibleQuizzes.map((quiz, idx) => {
            const title = quiz?.title || `Quiz ${idx + 1}`;
            const slug = quiz?.slug || makeSlugWithId(title, quiz?.id ?? idx);
            const id = quiz?.id ?? "";
            const attemptKey = makeTopicQuizAttemptKey({
              subject: subjectName,
              topic: topicName,
              slug,
              id,
            });
            const lastAttempt =
              attemptSummaries[attemptKey] || getQuizAttemptSummary(attemptKey, currentUserId);
            const attempted = Boolean(lastAttempt) || isQuizAttempted(attemptKey, currentUserId);
            const lastAttemptScore = clampPercent(
              lastAttempt?.totalMarks ? (Number(lastAttempt?.score || 0) / lastAttempt.totalMarks) * 100 : 0
            );
            const isPassingScore = lastAttemptScore >= 60;
            const attemptAccent = isPassingScore
              ? "rgba(46, 175, 83, 0.18)"
              : "rgba(245, 158, 11, 0.18)";
            const attemptGlow = isPassingScore
              ? "rgba(46, 175, 83, 0.24)"
              : "rgba(245, 158, 11, 0.22)";
            const basePath = `/quiz-zone/${encodePath(subjectName)}/${encodePath(
              topicName
            )}/${encodePath(slug)}`;
            const suffix = id ? `?id=${encodeURIComponent(id)}` : "";
            const leaderboardPath = `${basePath}/leaderboard${suffix}`;
            return (
              <div
                className={`quiz-list-row ${attempted ? "attempted-score-row" : ""}`}
                key={`${slug}-${idx}`}
                style={
                  attempted
                    ? {
                        "--attempt-fill": `${lastAttemptScore}%`,
                        "--attempt-accent": attemptAccent,
                        "--attempt-glow": attemptGlow,
                      }
                    : undefined
                }
              >
                <div className="quiz-title-wrap">
                  <Link
                    className={`quiz-title-link ${attempted ? "attempted-link" : ""}`}
                    to={`${basePath}/instructions${suffix}`}
                    onClick={(event) =>
                      handleProtectedQuizNavigation(event, `${basePath}/instructions${suffix}`)
                    }
                  >
                    {title}
                  </Link>
                  {attempted && lastAttempt ? (
                    <div className="quiz-last-attempt-card" aria-label="Last attempt score">
                      <div className="quiz-last-attempt-copy">
                        <span className="quiz-last-attempt-label">Your last attempt</span>
                        <div className="quiz-last-attempt-stats">
                          <strong>{`${Math.round(lastAttemptScore)}%`}</strong>
                          <span>{`${lastAttempt?.score || 0} / ${lastAttempt?.totalMarks || 0}`}</span>
                        </div>
                        {lastAttempt?.attemptNumber ? (
                          <span>{`Attempt #${lastAttempt.attemptNumber}`}</span>
                        ) : null}
                        <span>{`${lastAttempt?.correctAnswers || 0} correct in ${formatDuration(
                          lastAttempt?.timeTakenSeconds
                        )}`}</span>
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="quiz-leaderboard-actions">
                  {attempted ? (
                    <Link
                      className="quiz-secondary-btn"
                      to={leaderboardPath}
                      onClick={(event) =>
                        handleProtectedQuizNavigation(event, leaderboardPath)
                      }
                    >
                      View Score
                    </Link>
                  ) : null}
                  <Link
                    className="quiz-start-btn"
                    to={`${basePath}/start${suffix}`}
                    onClick={(event) =>
                      handleProtectedQuizNavigation(event, `${basePath}/start${suffix}`)
                    }
                  >
                    {attempted ? "Reattempt Quiz" : "Start Quiz"}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {!loading && !error ? (
        <>
          <div className="quiz-list-card quiz-content-section">
            <p className="quiz-content-lead">{topicSupportContent.overview}</p>
            <p className="quiz-content-paragraph">{topicSupportContent.whyThisMatters}</p>
            <div className="quiz-content-grid">
              <section className="quiz-content-panel" aria-label="What you will improve">
                <h2>What You Will Improve</h2>
                <ul className="quiz-content-list">
                  {topicSupportContent.skills.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
              <section className="quiz-content-panel" aria-label="How to use this page">
                <h2>How To Practice Better</h2>
                <ul className="quiz-content-list">
                  {topicSupportContent.studyPlan.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            </div>
          </div>
          <div className="quiz-list-card quiz-content-section">
            <h2>Why Learners Use This Topic Page</h2>
            <div className="quiz-content-tag-row" aria-label="Learning benefits">
              {topicSupportContent.trustSignals.map((item) => (
                <span key={item} className="quiz-content-tag">
                  {item}
                </span>
              ))}
            </div>
            <p className="quiz-content-paragraph">
              Each quiz on this page helps you revise {topicName} in a focused format. Instead of
              reading notes passively, you can test recall, compare results across attempts, and
              learn from the explanation panel after submission.
            </p>
          </div>
          <div className="quiz-list-card quiz-content-section">
            <h2>{topicName} Practice FAQ</h2>
            <div className="quiz-faq-list">
              {topicSupportContent.faq.map((item) => (
                <section key={item.question} className="quiz-faq-item">
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </section>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

export function TopicQuizInstructionsPage() {
  const { subject, topic, quizSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quizId = searchParams.get("id");
  const subjectName = decodePath(subject);
  const topicName = decodePath(topic);
  const slug = decodePath(quizSlug);
  const listPath = `/quiz-zone/${encodePath(subjectName)}/${encodePath(topicName)}/quizzes`;

  const [quizTitle, setQuizTitle] = useState("Quiz");
  const [language, setLanguage] = useState("hi");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const canonicalUrl = buildCanonicalUrl(
    `/quiz-zone/${encodePath(subjectName)}/${encodePath(topicName)}/${encodePath(slug)}/instructions`
  );
  const pageTitle = `${quizTitle} Instructions | AAMSchool`;
  const pageDescription = `Read instructions for ${quizTitle} on AAMSchool before starting the ${topicName} quiz.`;
  const instructionSupportContent = useMemo(
    () => buildTopicSupportContent(subjectName, topicName, 1),
    [subjectName, topicName]
  );

  useEffect(() => {
    if (isAuthenticated()) return;
    redirectToLoginForQuiz(navigate, `${location.pathname}${location.search}`);
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!isAuthenticated()) return undefined;
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchQuizDetails(slug, quizId);
        if (!mounted) return;
        setQuizTitle(data?.title || data?.quizTitle || "Quiz");
      } catch (err) {
        if (!mounted) return;
        setError(err.message || "Failed to load quiz details.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [quizId, slug]);

  const startPath = `/quiz-zone/${encodePath(subjectName)}/${encodePath(topicName)}/${encodePath(
    slug
  )}/start?id=${encodeURIComponent(quizId || "")}&lang=${language}`;

  return (
    <div className="quiz-page">
      <Helmet>
        <title>{pageTitle}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="noindex, nofollow" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
      </Helmet>
      <QuizBackButton onClick={() => navigate(listPath)} label="Back To Quiz List" />
      <h1>{quizTitle}</h1>
      <p className="quiz-meta">
        {subjectName} / {topicName}
      </p>

      {loading ? <p>Loading instructions...</p> : null}
      {error ? <p className="quiz-error">{error}</p> : null}

      {!loading && !error && (
        <div className="quiz-instructions">
          <h3>General Instructions</h3>
          <ul>
            <li>Read each question carefully before selecting your answer.</li>
            <li>First attempt captures your responses only; solution is available after submit.</li>
            <li>Use Next/Previous to navigate questions.</li>
            <li>On reattempt, correct/wrong options and description are shown instantly.</li>
          </ul>

          <div className="lang-selector">
            <label htmlFor="topic-quiz-lang">Choose Language:</label>
            <select
              id="topic-quiz-lang"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              <option value="hi">Hindi (Default)</option>
              <option value="en">English</option>
            </select>
          </div>

          <div className="quiz-actions">
            <button type="button" className="quiz-start-btn" onClick={() => navigate(startPath)}>
              Begin Quiz
            </button>
          </div>

          <div className="quiz-content-grid">
            <section className="quiz-content-panel" aria-label="Before you begin">
              <h2>Before You Begin</h2>
              <p className="quiz-content-paragraph">
                {instructionSupportContent.overview}
              </p>
              <ul className="quiz-content-list">
                {instructionSupportContent.studyPlan.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
            <section className="quiz-content-panel" aria-label="How this quiz helps">
              <h2>How This Quiz Helps</h2>
              <p className="quiz-content-paragraph">
                After you submit, use the score report, difficulty analysis, and solution panel to
                understand where your preparation in {topicName} is strong and where you need one
                more revision round.
              </p>
              <ul className="quiz-content-list">
                {instructionSupportContent.skills.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}

export function TopicQuizPlayPage() {
  const { subject, topic, quizSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quizId = searchParams.get("id");
  const language = searchParams.get("lang") || "hi";
  const subjectName = decodePath(subject);
  const topicName = decodePath(topic);
  const slug = decodePath(quizSlug);
  const canonicalUrl = buildCanonicalUrl(
    `/quiz-zone/${encodePath(subjectName)}/${encodePath(topicName)}/${encodePath(slug)}/start`
  );
  const instructionsPath = `/quiz-zone/${encodePath(subjectName)}/${encodePath(topicName)}/${encodePath(
    slug
  )}/instructions?id=${encodeURIComponent(quizId || "")}`;

  const [quiz, setQuiz] = useState(null);
  const [quizLanguage, setQuizLanguage] = useState(normalizeLanguage(language));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [hindiQuiz, setHindiQuiz] = useState(null);
  const [isHindiReady, setIsHindiReady] = useState(false);
  const [translationStatus, setTranslationStatus] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
  const [isWrapConfirmOpen, setIsWrapConfirmOpen] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);

  useEffect(() => {
    if (isAuthenticated()) return;
    redirectToLoginForQuiz(navigate, `${location.pathname}${location.search}`);
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!isAuthenticated()) return undefined;
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchQuizDetails(slug, quizId);
        if (!mounted) return;
        setQuiz(data);
      } catch (err) {
        if (!mounted) return;
        setError(err.message || "Failed to load quiz.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [quizId, slug]);

  const questions = useMemo(() => quiz?.questions || [], [quiz]);
  const normalizedQuestions = useMemo(
    () => (Array.isArray(questions) ? questions.map(normalizeMcqQuestion) : []),
    [questions]
  );
  const hasQuestionsNeedingTranslation = useMemo(
    () =>
      normalizedQuestions.some(
        (question) => getQuestionSourceLanguage(question) !== quizLanguage
      ),
    [normalizedQuestions, quizLanguage]
  );
  const displayedQuestions =
    hasQuestionsNeedingTranslation && isHindiReady && hindiQuiz?.questions
      ? hindiQuiz.questions
      : normalizedQuestions;
  const currentQuestion = normalizedQuestions[currentIndex];
  const currentDisplayedQuestion = displayedQuestions[currentIndex] || currentQuestion;
  const selectedOption = userAnswers[currentIndex] || null;
  const currentExplanationPayload = getExplanationPayload(currentDisplayedQuestion || currentQuestion);
  const isLastQuestion = currentIndex === normalizedQuestions.length - 1;
  const shouldBlockForHindiTranslation = hasQuestionsNeedingTranslation && !isHindiReady;
  const totalQuizSeconds = normalizedQuestions.length * 60;
  const attemptCacheKey = useMemo(
    () =>
      makeTopicQuizAttemptKey({
        subject: subjectName,
        topic: topicName,
        slug,
        id: quizId || quiz?.id || "",
      }),
    [quiz?.id, quizId, slug, subjectName, topicName]
  );
  const isReviewMode = false;

  const attemptedCount = Object.keys(userAnswers).filter((key) => userAnswers[key]).length;

  useEffect(() => {
    if (!totalQuizSeconds) {
      setRemainingSeconds(null);
      return;
    }
    setRemainingSeconds(totalQuizSeconds);
    setHasAutoSubmitted(false);
  }, [totalQuizSeconds]);

  const handleSelectOption = (optionIndex) => {
    const selectedChoice = normalizedQuestions[currentIndex]?.choices?.[optionIndex];
    if (!selectedChoice?.text) return;
    setSubmitError("");
    setUserAnswers((prev) => ({
      ...prev,
      [currentIndex]: {
        choiceId: selectedChoice.id,
        text: selectedChoice.text,
      },
    }));
  };

  const getOptionClass = (optionIndex) => {
    const choice = normalizedQuestions[currentIndex]?.choices?.[optionIndex];
    if (!choice?.text) return "quiz-option";
    if (!selectedOption) return "quiz-option";
    if (!isReviewMode) {
      return selectedOption?.choiceId === choice.id ? "quiz-option selected" : "quiz-option";
    }
    if (
      (currentQuestion?.correct_choice_id !== null && choice.id === currentQuestion.correct_choice_id) ||
      choice.text === currentQuestion.correct_option
    ) {
      return "quiz-option correct";
    }
    if (
      (selectedOption?.choiceId !== undefined && choice.id === selectedOption.choiceId) ||
      choice.text === selectedOption?.text
    ) {
      if (
        (currentQuestion?.correct_choice_id !== null && choice.id === currentQuestion.correct_choice_id) ||
        choice.text === currentQuestion.correct_option
      ) {
        return "quiz-option correct";
      }
      return "quiz-option wrong";
    }
    return "quiz-option";
  };

  const handleNext = () => {
    if (isLastQuestion) {
      setIsWrapConfirmOpen(true);
      return;
    }
    setCurrentIndex((prev) => Math.min(prev + 1, normalizedQuestions.length - 1));
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const submitQuizAttempt = useCallback(async ({ autoSubmit = false } = {}) => {
    if (isSubmitting) {
      return;
    }
    const resolvedQuizId = Number(quizId || quiz?.id || 0);
    if (!resolvedQuizId) {
      setSubmitError("Unable to submit this quiz right now. Quiz ID is missing.");
      return;
    }

    const answers = normalizedQuestions
      .map((question, index) => {
        const selectedAnswer = userAnswers[index];
        if (
          !selectedAnswer ||
          question?.quizQuestionId == null ||
          selectedAnswer?.choiceId == null
        ) {
          return null;
        }
        return {
          quizQuestionId: question.quizQuestionId,
          selectedChoiceId: selectedAnswer.choiceId,
        };
      })
      .filter(Boolean);

    setIsSubmitting(true);
    setSubmitError("");
    setIsFinishModalOpen(false);

    try {
      const currentUser = getUser();
      const elapsedSeconds = Math.max(1, totalQuizSeconds - Math.max(remainingSeconds, 0));
      const payload = {
        timeTakenSeconds: autoSubmit ? Math.max(totalQuizSeconds, 1) : elapsedSeconds,
        answers,
      };

      if (currentUser?.id) {
        payload.userId = currentUser.id;
      }

      const response = await apiFetch(`/quizzes/${resolvedQuizId}/submit`, {
        method: "POST",
        auth: true,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await readJsonSafe(response);
        throw new Error(data?.message || "Unable to submit quiz. Please try again.");
      }

      const responseData = await readJsonSafe(response);
      const summary = buildAttemptSummary({
        responseData,
        normalizedQuestions,
        userAnswers,
        timeTakenSeconds: payload.timeTakenSeconds,
        currentUserId: currentUser?.id,
      });
      markQuizAttempted(attemptCacheKey, summary);
      saveAttemptSolutions(attemptCacheKey, {
        savedAt: new Date().toISOString(),
        quizTitle: quiz?.title || "",
        answers: normalizedQuestions.map((question, index) => {
          const selected = userAnswers[index];
          const selectedText = selected?.text || "";
          const isCorrectById =
            selected?.choiceId != null &&
            question?.correct_choice_id != null &&
            selected.choiceId === question.correct_choice_id;
          const isCorrectByText =
            selectedText && question?.correct_option && selectedText === question.correct_option;
          return {
            question: question?.question || "",
            options: Array.isArray(question?.options) ? question.options : [],
            selectedOption: selectedText,
            correctOption: question?.correct_option || "",
            detailsDescription: question?.details_description || "",
            isCorrect: Boolean(isCorrectById || isCorrectByText),
          };
        }),
      });

      navigate(
        `/quiz-zone/${encodePath(subjectName)}/${encodePath(topicName)}/${encodePath(
          slug
        )}/leaderboard?id=${encodeURIComponent(resolvedQuizId)}`,
        {
          replace: true,
          state: {
            submitMessage: responseData?.attemptNumber
              ? `Attempt #${responseData.attemptNumber} submitted successfully.`
              : "Quiz submitted successfully.",
          },
        }
      );
    } catch (err) {
      setSubmitError(err.message || "Unable to submit quiz. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    attemptCacheKey,
    isSubmitting,
    navigate,
    normalizedQuestions,
    quiz?.id,
    quizId,
    remainingSeconds,
    slug,
    subjectName,
    topicName,
    totalQuizSeconds,
    userAnswers,
    quiz?.title,
  ]);

  const handleFinish = () => {
    if (isSubmitting) {
      return;
    }
    setIsFinishModalOpen(true);
  };

  const handleQuestionLanguageChange = (value) => {
    setQuizLanguage(normalizeLanguage(value));
  };

  const getQuestionStatusClass = (index) => {
    const selected = userAnswers[index];
    if (!selected) return "unattempted";
    if (!isReviewMode) return "selected";
    const question = normalizedQuestions[index];
    const selectedChoice = question?.choices?.find((choice) => choice?.id === selected?.choiceId);
    const selectedText = selectedChoice?.text || selected?.text || "";
    const correctText = question?.correct_option || "";
    const isCorrectById =
      selected?.choiceId != null &&
      question?.correct_choice_id != null &&
      selected.choiceId === question.correct_choice_id;
    const isCorrectByText = areOptionsEqual(selectedText, correctText);
    return isCorrectById || isCorrectByText ? "correct" : "wrong";
  };

  useEffect(() => {
    setQuizLanguage(normalizeLanguage(language));
  }, [language]);

  useEffect(() => {
    if (
      loading ||
      error ||
      isSubmitting ||
      shouldBlockForHindiTranslation ||
      !normalizedQuestions.length ||
      remainingSeconds == null
    ) {
      return undefined;
    }

    if (remainingSeconds <= 0) {
      if (hasAutoSubmitted) {
        return undefined;
      }
      setHasAutoSubmitted(true);
      submitQuizAttempt({ autoSubmit: true });
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setRemainingSeconds((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [
    error,
    hasAutoSubmitted,
    isSubmitting,
    loading,
    normalizedQuestions.length,
    remainingSeconds,
    submitQuizAttempt,
    shouldBlockForHindiTranslation,
  ]);

  useEffect(() => {
    let mounted = true;

    const buildHindiQuestions = async () => {
      if (!normalizedQuestions.length) return;

      if (!hasQuestionsNeedingTranslation) {
        if (!mounted) return;
        setHindiQuiz(null);
        setIsHindiReady(true);
        setTranslationStatus("");
        return;
      }

      setIsHindiReady(false);
      setTranslationStatus("Preparing translation...");

      const cacheKey = `${HINDI_QUIZ_CACHE_PREFIX}${quiz?.id || slug || "quiz"}::${quizLanguage}`;
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (
            parsed &&
            Array.isArray(parsed.questions) &&
            parsed.questions.length === normalizedQuestions.length
          ) {
            if (!mounted) return;
            setHindiQuiz(parsed);
            setIsHindiReady(true);
            setTranslationStatus("");
            return;
          }
        }
      } catch {
        // Ignore cache read/parse issues and rebuild translation.
      }

      try {
        const translatedTitle =
          quizLanguage === "hi" ? await translateText(quiz?.title, "hi", "en") : quiz?.title;
        const translated = await mapWithConcurrency(
          normalizedQuestions,
          3,
          async (q, idx) => {
            const sourceLanguage = getQuestionSourceLanguage(q);
            const [translatedQuestion, translatedDescription, translatedOptions] =
              await Promise.all([
                translateQuestionToHindi(q.question, quizLanguage, sourceLanguage),
                translateText(q.details_description, quizLanguage, sourceLanguage),
                Promise.all(
                  (q.options || []).map((option) =>
                    translateOptionToHindi(option, quizLanguage, sourceLanguage)
                  )
                ),
              ]);

            if (mounted && idx % 5 === 0) {
              setTranslationStatus(
                `Translating questions... ${Math.min(
                  idx + 1,
                  normalizedQuestions.length
                )}/${normalizedQuestions.length}`
              );
            }

            return {
              ...q,
              question: translatedQuestion || q.question,
              details_description: translatedDescription || q.details_description,
              options: translatedOptions.length ? translatedOptions : q.options,
            };
          }
        );
        if (!mounted) return;
        const translatedQuiz = {
          ...quiz,
          title: translatedTitle || quiz?.title,
          questions: translated,
        };
        setHindiQuiz(translatedQuiz);
        setIsHindiReady(true);
        setTranslationStatus("");
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(translatedQuiz));
        } catch {
          // Ignore cache write failures.
        }
      } catch {
        if (!mounted) return;
        setHindiQuiz(null);
        setIsHindiReady(false);
        setTranslationStatus("Translation unavailable right now. Showing original language.");
      } finally {
        if (mounted) {
          setTranslationStatus("");
        }
      }
    };

    buildHindiQuestions();

    return () => {
      mounted = false;
    };
  }, [hasQuestionsNeedingTranslation, normalizedQuestions, quiz, quizLanguage, slug]);

  const displayedTitle =
    quizLanguage === "hi" && isHindiReady && hindiQuiz?.title
      ? hindiQuiz.title
      : quiz?.title || "Quiz";
  const timerLabel = formatDuration(remainingSeconds ?? totalQuizSeconds);

  if (loading) {
    return (
      <div className="quiz-page">
        <Helmet>
          <title>{`Loading Quiz | ${topicName} | AAMSchool`}</title>
          <link rel="canonical" href={canonicalUrl} />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <QuizBackButton onClick={() => navigate(instructionsPath)} />
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quiz-page">
        <Helmet>
          <title>{`${topicName} Quiz | AAMSchool`}</title>
          <link rel="canonical" href={canonicalUrl} />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <QuizBackButton onClick={() => navigate(instructionsPath)} />
        <p className="quiz-error">{error}</p>
      </div>
    );
  }

  if (!quiz || !Array.isArray(normalizedQuestions) || normalizedQuestions.length === 0) {
    return (
      <div className="quiz-page">
        <Helmet>
          <title>{`${topicName} Quiz | AAMSchool`}</title>
          <link rel="canonical" href={canonicalUrl} />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <QuizBackButton onClick={() => navigate(instructionsPath)} />
        <p className="quiz-error">No questions found for this quiz.</p>
      </div>
    );
  }

  return (
    <div className="quiz-page">
      <Helmet>
        <title>{`${quiz?.title || topicName} Quiz Attempt | AAMSchool`}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta
          name="description"
          content={`Attempt ${quiz?.title || topicName} quiz on AAMSchool.`}
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <QuizBackButton onClick={() => navigate(instructionsPath)} />
      <div className="quiz-list-card quiz-focus-shell">
        <div>
          <h1>{displayedTitle}</h1>
          <p className="quiz-meta">
            Focus mode is active. The quiz is running in a modal with a live timer.
          </p>
        </div>
        <div className="quiz-focus-stats">
          <div className="quiz-focus-pill">
            <span>Questions</span>
            <strong>{normalizedQuestions.length}</strong>
          </div>
          <div className="quiz-focus-pill warning">
            <span>Time Left</span>
            <strong>{timerLabel}</strong>
          </div>
        </div>
      </div>

      <div className="quiz-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="quiz-modal-title">
        <div className="quiz-modal-card quiz-runner-modal">
          <div className="quiz-modal-header">
            <div>
              <p className="quiz-modal-eyebrow">Live Quiz</p>
              <h2 id="quiz-modal-title">{displayedTitle}</h2>
            </div>
            <div className={`quiz-timer-chip ${remainingSeconds <= 60 ? "is-danger" : ""}`}>
              <span>Time Left</span>
              <strong>{timerLabel}</strong>
            </div>
          </div>

          {shouldBlockForHindiTranslation ? (
            <div className="quiz-loading-overlay" role="status" aria-live="polite">
              <div className="quiz-loading-spinner" />
              <p>{translationStatus || "Translating quiz to selected language. Please wait..."}</p>
            </div>
          ) : (
            <>
              <div className="quiz-dot-progress" aria-label="Question progress">
                <div className="quiz-progress-track">
                  {normalizedQuestions.map((_, idx) => (
                    <span
                      key={`seg-${idx}`}
                      className={`quiz-progress-segment ${userAnswers[idx] ? "attempted" : ""} ${
                        idx === currentIndex ? "active" : ""
                      }`}
                    />
                  ))}
                </div>
                <span className="quiz-progress-count">
                  {currentIndex + 1}/{normalizedQuestions.length}
                </span>
              </div>

              <div className="quiz-runner-layout">
                <div className="quiz-question-card quiz-question-card-embedded">
                  <div className="question-lang-switch" role="group" aria-label="Question language switch">
                    <button
                      type="button"
                      className={`question-lang-pill ${quizLanguage === "hi" ? "active" : ""}`}
                      onClick={() => handleQuestionLanguageChange("hi")}
                      aria-pressed={quizLanguage === "hi"}
                    >
                      HI
                    </button>
                    <button
                      type="button"
                      className={`question-lang-pill ${quizLanguage === "en" ? "active" : ""}`}
                      onClick={() => handleQuestionLanguageChange("en")}
                      aria-pressed={quizLanguage === "en"}
                    >
                      EN
                    </button>
                  </div>

                  {quizLanguage === "hi" && !isHindiReady && (
                    <p className="translation-note">
                      {translationStatus || "Translating in background..."}
                    </p>
                  )}

                  <h3>{currentDisplayedQuestion?.question || currentQuestion.question}</h3>
                  <div className="quiz-options">
                    {(currentDisplayedQuestion?.options || []).map((option, idx) => (
                      <button
                        type="button"
                        key={`${idx}-${option}`}
                        className={getOptionClass(idx)}
                        onClick={() => handleSelectOption(idx)}
                        disabled={isSubmitting}
                      >
                        {option}
                      </button>
                    ))}
                  </div>

                  <div className="quiz-actions">
                    <button
                      type="button"
                      className="quiz-secondary-btn"
                      onClick={handlePrevious}
                      disabled={currentIndex === 0 || isSubmitting}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="quiz-start-btn"
                      onClick={handleNext}
                      disabled={isSubmitting}
                    >
                      {isLastQuestion ? "Next (Last Question)" : "Next Question"}
                    </button>
                    <button
                      type="button"
                      className="quiz-finish-btn"
                      onClick={handleFinish}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Finish Quiz"}
                    </button>
                  </div>

                  {submitError ? <p className="quiz-error">{submitError}</p> : null}

                  {selectedOption &&
                  isReviewMode &&
                  (currentExplanationPayload.text ||
                    (currentExplanationPayload.diagramType === "DIRECTION_SENSE_SVG" &&
                      currentExplanationPayload.diagramData)) ? (
                    <div className="quiz-explanation">
                      <strong>Explanation:</strong>
                      <ExplanationContent
                        text={currentExplanationPayload.text}
                        diagramType={currentExplanationPayload.diagramType}
                        diagramData={currentExplanationPayload.diagramData}
                      />
                    </div>
                  ) : null}
                </div>
                <aside className="quiz-solution-nav" aria-label="Question number panel">
                  <div className="quiz-solution-nav-title">Questions</div>
                  <div className="quiz-solution-nav-grid">
                    {normalizedQuestions.map((_, idx) => (
                      <button
                        key={`play-nav-${idx}`}
                        type="button"
                        className={`quiz-solution-index ${getQuestionStatusClass(idx)} ${idx === currentIndex ? "active" : ""}`}
                        onClick={() => setCurrentIndex(idx)}
                        disabled={isSubmitting}
                      >
                        {idx + 1}
                      </button>
                    ))}
                  </div>
                </aside>
              </div>
            </>
          )}
        </div>
      </div>

      {isFinishModalOpen ? (
        <div className="quiz-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="quiz-confirm-title">
          <div className="quiz-confirm-card">
            <p className="quiz-modal-eyebrow">Confirm Submission</p>
            <h2 id="quiz-confirm-title">Submit quiz now?</h2>
            <p className="quiz-meta">
              You have attempted {attemptedCount} of {normalizedQuestions.length} questions. If this was a mistake,
              you can continue the quiz.
            </p>
            <div className="quiz-actions">
              <button
                type="button"
                className="quiz-secondary-btn"
                onClick={() => setIsFinishModalOpen(false)}
                disabled={isSubmitting}
              >
                Continue Quiz
              </button>
              <button
                type="button"
                className="quiz-finish-btn"
                onClick={() => submitQuizAttempt()}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Quiz"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {isWrapConfirmOpen ? (
        <div className="quiz-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="quiz-wrap-title">
          <div className="quiz-confirm-card">
            <p className="quiz-modal-eyebrow">Last Question</p>
            <h2 id="quiz-wrap-title">Move to first question?</h2>
            <p className="quiz-meta">
              You are on the last question. Do you want to go back to question 1?
            </p>
            <div className="quiz-actions">
              <button
                type="button"
                className="quiz-secondary-btn"
                onClick={() => setIsWrapConfirmOpen(false)}
                disabled={isSubmitting}
              >
                Stay Here
              </button>
              <button
                type="button"
                className="quiz-start-btn"
                onClick={() => {
                  setCurrentIndex(0);
                  setIsWrapConfirmOpen(false);
                }}
                disabled={isSubmitting}
              >
                Go To First
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function TopicQuizLeaderboardPage() {
  const { subject, topic, quizSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quizIdFromQuery = searchParams.get("id");
  const subjectName = decodePath(subject);
  const topicName = decodePath(topic);
  const slug = decodePath(quizSlug);
  const canonicalUrl = buildCanonicalUrl(
    `/quiz-zone/${encodePath(subjectName)}/${encodePath(topicName)}/${encodePath(slug)}/leaderboard`
  );
  const listPath = `/quiz-zone/${encodePath(subjectName)}/${encodePath(topicName)}/quizzes`;

  const [quizMeta, setQuizMeta] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [myAttempts, setMyAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [toastMessage, setToastMessage] = useState(location.state?.submitMessage || "");
  const [solutionPayload, setSolutionPayload] = useState(null);
  const [isSolutionOpen, setIsSolutionOpen] = useState(false);
  const [solutionQuestions, setSolutionQuestions] = useState([]);
  const [solutionIndex, setSolutionIndex] = useState(0);
  const [solutionLanguage, setSolutionLanguage] = useState("en");
  const [solutionHindiQuestions, setSolutionHindiQuestions] = useState([]);
  const [isSolutionHindiReady, setIsSolutionHindiReady] = useState(true);
  const [solutionTranslationStatus, setSolutionTranslationStatus] = useState("");

  useEffect(() => {
    if (quizIdFromQuery) return undefined;

    let mounted = true;

    const loadQuizMeta = async () => {
      try {
        const data = await fetchQuizDetails(slug, null);
        if (!mounted) return;
        setQuizMeta({
          id: data?.id || null,
          title: data?.title || "Quiz Leaderboard",
        });
      } catch {
        if (!mounted) return;
        setQuizMeta({
          id: null,
          title: "Quiz Leaderboard",
        });
      }
    };

    loadQuizMeta();

    return () => {
      mounted = false;
    };
  }, [quizIdFromQuery, slug]);

  const resolvedQuizId = Number(quizIdFromQuery || quizMeta?.id || 0);
  const currentUserId = getUser()?.id;
  const attemptCacheKey = useMemo(
    () =>
      makeTopicQuizAttemptKey({
        subject: subjectName,
        topic: topicName,
        slug,
        id: resolvedQuizId || "",
      }),
    [resolvedQuizId, slug, subjectName, topicName]
  );
  const startPath = `/quiz-zone/${encodePath(subjectName)}/${encodePath(topicName)}/${encodePath(
    slug
  )}/start?id=${encodeURIComponent(resolvedQuizId || "")}&lang=hi`;

  const loadLeaderboard = async ({ silent = false } = {}) => {
    if (!resolvedQuizId) {
      setError("Unable to load leaderboard. Quiz ID is missing.");
      setLoading(false);
      return;
    }

    if (silent) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }

    setError("");

    try {
      const data = await fetchQuizLeaderboard(resolvedQuizId, { auth: isAuthenticated() });
      setLeaderboard(data);
    } catch (err) {
      setError(err.message || "Unable to load leaderboard. Please try again.");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (!resolvedQuizId) {
      if (quizIdFromQuery || quizMeta) {
        setLoading(false);
        setError("Unable to load leaderboard. Quiz ID is missing.");
      }
      return;
    }

    let mounted = true;

    const loadInitialLeaderboard = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await fetchQuizLeaderboard(resolvedQuizId, { auth: isAuthenticated() });
        if (!mounted) return;
        setLeaderboard(data);
      } catch (err) {
        if (!mounted) return;
        setError(err.message || "Unable to load leaderboard. Please try again.");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadInitialLeaderboard();

    return () => {
      mounted = false;
    };
  }, [quizMeta, quizIdFromQuery, resolvedQuizId]);

  useEffect(() => {
    if (!isAuthenticated() || !resolvedQuizId) {
      setMyAttempts([]);
      return undefined;
    }

    let mounted = true;
    const loadAttempts = async () => {
      try {
        const attempts = await fetchMyQuizAttempts(resolvedQuizId);
        if (!mounted) return;
        setMyAttempts(attempts);
        const latestSummary = buildAttemptSummaryFromAttempt(attempts[0], currentUserId);
        if (latestSummary) {
          markQuizAttempted(attemptCacheKey, latestSummary);
        }
      } catch {
        if (!mounted) return;
        setMyAttempts([]);
      }
    };

    loadAttempts();
    return () => {
      mounted = false;
    };
  }, [attemptCacheKey, currentUserId, resolvedQuizId]);

  useEffect(() => {
    if (!isAuthenticated() || !resolvedQuizId) return;
    setSolutionPayload(readAttemptSolutions(attemptCacheKey));
  }, [attemptCacheKey, resolvedQuizId]);

  useEffect(() => {
    if (!isAuthenticated() || !resolvedQuizId) return undefined;

    let mounted = true;
    const loadQuestions = async () => {
      try {
        const data = await fetchQuizDetails(slug, resolvedQuizId);
        if (!mounted) return;
        const normalized = Array.isArray(data?.questions)
          ? data.questions.map(normalizeMcqQuestion)
          : [];
        setSolutionQuestions(normalized);
      } catch {
        if (!mounted) return;
        setSolutionQuestions([]);
      }
    };
    loadQuestions();
    return () => {
      mounted = false;
    };
  }, [resolvedQuizId, slug]);

  useEffect(() => {
    let mounted = true;

    const buildHindiSolutions = async () => {
      if (solutionLanguage !== "hi") {
        if (!mounted) return;
        setIsSolutionHindiReady(true);
        setSolutionTranslationStatus("");
        return;
      }
      if (!solutionQuestions.length) {
        if (!mounted) return;
        setSolutionHindiQuestions([]);
        setIsSolutionHindiReady(true);
        setSolutionTranslationStatus("");
        return;
      }

      setIsSolutionHindiReady(false);
      setSolutionTranslationStatus("Translating solutions...");

      try {
        const translated = await mapWithConcurrency(solutionQuestions, 3, async (q) => {
          const sourceLanguage = getQuestionSourceLanguage(q);
          const [questionText, descriptionText, translatedOptions] = await Promise.all([
            translateQuestionToHindi(q.question, "hi", sourceLanguage),
            translateText(q.details_description, "hi", sourceLanguage),
            Promise.all((q.options || []).map((option) => translateOptionToHindi(option, "hi", sourceLanguage))),
          ]);
          return {
            ...q,
            question: questionText || q.question,
            details_description: descriptionText || q.details_description,
            options: translatedOptions.length ? translatedOptions : q.options,
          };
        });
        if (!mounted) return;
        setSolutionHindiQuestions(translated);
        setIsSolutionHindiReady(true);
        setSolutionTranslationStatus("");
      } catch {
        if (!mounted) return;
        setSolutionHindiQuestions(solutionQuestions);
        setIsSolutionHindiReady(true);
        setSolutionTranslationStatus("");
      }
    };

    buildHindiSolutions();
    return () => {
      mounted = false;
    };
  }, [solutionLanguage, solutionQuestions]);

  const title = leaderboard?.quizTitle || quizMeta?.title || "Quiz Leaderboard";
  const entries = useMemo(
    () => (Array.isArray(leaderboard?.entries) ? leaderboard.entries : []),
    [leaderboard?.entries]
  );
  const currentUserEntry = entries.find(
    (entry) => entry?.isCurrentUser || String(entry?.user?.userId ?? "") === String(currentUserId ?? "")
  );
  const canOpenSolution =
    Boolean(currentUserEntry || myAttempts.length) &&
    Array.isArray(solutionPayload?.answers) &&
    solutionPayload.answers.length > 0 &&
    solutionQuestions.length > 0;
  const displayedSolutionQuestions =
    solutionLanguage === "hi"
      ? isSolutionHindiReady
        ? solutionHindiQuestions
        : []
      : solutionQuestions;
  const activeSolutionQuestion = displayedSolutionQuestions[solutionIndex] || null;
  const canonicalSolutionQuestion = solutionQuestions[solutionIndex] || null;
  const totalSolutionQuestions = displayedSolutionQuestions.length;
  const difficultyStats = useMemo(() => {
    const buckets = {
      easy: { total: 0, attempted: 0, correct: 0, wrong: 0, unattempted: 0, questions: [] },
      medium: { total: 0, attempted: 0, correct: 0, wrong: 0, unattempted: 0, questions: [] },
      hard: { total: 0, attempted: 0, correct: 0, wrong: 0, unattempted: 0, questions: [] },
    };
    solutionQuestions.forEach((question, idx) => {
      const key = normalizeDifficultyLabel(question?.difficulty || question?.level);
      const selected = solutionPayload?.answers?.[idx]?.selectedOption || "";
      const correctOption = question?.correct_option || "";
      const status = !selected
        ? "unattempted"
        : areOptionsEqual(selected, correctOption)
        ? "correct"
        : "wrong";
      buckets[key].total += 1;
      buckets[key].questions.push({
        index: idx,
        number: idx + 1,
        status,
      });
      if (!selected) {
        buckets[key].unattempted += 1;
        return;
      }
      buckets[key].attempted += 1;
      if (areOptionsEqual(selected, correctOption)) {
        buckets[key].correct += 1;
      } else {
        buckets[key].wrong += 1;
      }
    });
    return buckets;
  }, [solutionPayload?.answers, solutionQuestions]);
  const derivedCorrect = solutionQuestions.reduce((count, question, idx) => {
    const selected = solutionPayload?.answers?.[idx]?.selectedOption || "";
    if (!selected) return count;
    return areOptionsEqual(selected, question?.correct_option) ? count + 1 : count;
  }, 0);
  const derivedAttempted = (solutionPayload?.answers || []).filter((item) => item?.selectedOption).length;
  const totalQuestions = solutionQuestions.length;
  const attemptSummary = useMemo(
    () => getQuizAttemptSummary(attemptCacheKey, currentUserId),
    [attemptCacheKey, currentUserId]
  );
  const latestAttemptSummary = useMemo(
    () => buildAttemptSummaryFromAttempt(myAttempts[0], currentUserId) || attemptSummary,
    [attemptSummary, currentUserId, myAttempts]
  );
  const statsSource = latestAttemptSummary || currentUserEntry || null;
  const statsScore = Number(statsSource?.score ?? derivedCorrect ?? 0);
  const statsTotalMarks = Number(statsSource?.totalMarks ?? totalQuestions ?? 0);
  const statsAttempted = Number(statsSource?.attemptedQuestions ?? derivedAttempted ?? 0);
  const statsCorrect = Number(statsSource?.correctAnswers ?? derivedCorrect ?? 0);
  const statsWrong = Number(statsSource?.wrongAnswers ?? Math.max(statsAttempted - statsCorrect, 0));
  const statsAccuracy = Number(
    statsSource?.accuracy ??
      (statsAttempted ? (statsCorrect / statsAttempted) * 100 : 0)
  );
  const leaderboardDummySessionKey = useMemo(() => {
    const attemptMarker =
      currentUserEntry?.submittedAt ||
      latestAttemptSummary?.submittedAt ||
      attemptSummary?.submittedAt ||
      attemptSummary?.attemptedAt ||
      "default";
    return `${attemptCacheKey}::${String(attemptMarker)}`;
  }, [
    attemptCacheKey,
    latestAttemptSummary?.submittedAt,
    attemptSummary?.attemptedAt,
    attemptSummary?.submittedAt,
    currentUserEntry?.submittedAt,
  ]);
  const leaderboardDisplayEntries = useMemo(
    () =>
      buildLeaderboardDisplayEntries(
        entries,
        statsTotalMarks || totalQuestions,
        leaderboardDummySessionKey
      ),
    [entries, leaderboardDummySessionKey, statsTotalMarks, totalQuestions]
  );
  const hasPreviewLeaderboardEntries = leaderboardDisplayEntries.some((entry) => entry.isSample);
  const scorePercent = clampPercent(statsTotalMarks ? (statsScore / statsTotalMarks) * 100 : 0);
  const performanceMessage =
    scorePercent >= 80
      ? "Excellent performance. Keep pushing higher."
      : scorePercent >= 50
      ? "Nice work. A quick review can lift your score further."
      : "Review the marked questions and try again with focus.";
  const openSolutionAtQuestion = (questionIndex) => {
    if (!canOpenSolution) return;
    setSolutionIndex(questionIndex);
    setSolutionLanguage("en");
    setIsSolutionOpen(true);
  };

  if (loading) {
    return (
      <div className="quiz-page">
        <Helmet>
          <title>{`Quiz Leaderboard | ${topicName} | AAMSchool`}</title>
          <link rel="canonical" href={canonicalUrl} />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <QuizBackButton onClick={() => navigate(listPath)} label="Back To Quiz List" />
        <div className="quiz-list-card">
          <div className="quiz-skeleton-line short" />
          <div className="quiz-skeleton-line medium" />
          <div className="quiz-skeleton-line long" />
        </div>
        <div className="quiz-list-card quiz-leaderboard-table-wrap">
          <div className="quiz-skeleton-row" />
          <div className="quiz-skeleton-row" />
          <div className="quiz-skeleton-row" />
          <div className="quiz-skeleton-row" />
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-page">
      <Helmet>
        <title>{`${title} Leaderboard | AAMSchool`}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta
          name="description"
          content={`Review leaderboard and performance for ${title} on AAMSchool.`}
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Toast message={toastMessage} onClose={() => setToastMessage("")} />
      <QuizBackButton onClick={() => navigate(listPath)} label="Back To Quiz List" />
      <div className="quiz-list-card quiz-leaderboard-header">
        <div>
          <h1>{title}</h1>
          <p className="quiz-meta">
            {subjectName} / {topicName}
          </p>
          <p className="quiz-leaderboard-meta">
            Participants: <strong>{leaderboard?.totalParticipants || 0}</strong>
            {" | "}
            Last updated: <strong>{formatSubmittedAt(leaderboard?.generatedAt)}</strong>
          </p>
        </div>
        <div className="quiz-leaderboard-actions">
          <button
            type="button"
            className="quiz-secondary-btn"
            onClick={() => loadLeaderboard({ silent: true })}
            disabled={isRefreshing}
          >
            {isRefreshing ? "Refreshing..." : "Refresh"}
          </button>
          <button
            type="button"
            className="quiz-start-btn"
            onClick={() =>
              isAuthenticated()
                ? navigate(startPath)
                : redirectToLoginForQuiz(navigate, startPath)
            }
          >
            Retake Quiz
          </button>
          <button
            type="button"
            className="quiz-secondary-btn"
            onClick={() => {
              setSolutionIndex(0);
              setSolutionLanguage("en");
              setIsSolutionOpen(true);
            }}
            disabled={!canOpenSolution}
          >
            Solution
          </button>
        </div>
      </div>

      {error ? (
        <div className="quiz-list-card">
          <p className="quiz-error">{error}</p>
          <div className="quiz-actions">
            <button type="button" className="quiz-start-btn" onClick={() => loadLeaderboard()}>
              Retry
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="quiz-report-layout">
            <div className="quiz-list-card quiz-stats-shell quiz-result-card">
              <p className="quiz-result-status">{performanceMessage}</p>
              <div className="quiz-score-hero">
                <div className="quiz-score-ring" style={{ "--score": `${scorePercent}%` }}>
                  <strong>{`${scorePercent.toFixed(0)}%`}</strong>
                  <span>Score</span>
                </div>
                <div className="quiz-score-text">
                  <h3>Result Overview</h3>
                  <p>
                    You answered <strong>{statsCorrect}</strong> out of <strong>{statsTotalMarks}</strong>{" "}
                    questions correctly with <strong>{statsWrong}</strong> wrong answers.
                  </p>
                </div>
              </div>
              <div className="quiz-stats-grid">
                <div className="quiz-summary-card info">
                  <span>Score</span>
                  <strong>{`${statsScore} / ${statsTotalMarks}`}</strong>
                </div>
                <div className="quiz-summary-card neutral">
                  <span>Attempted</span>
                  <strong>{statsAttempted}</strong>
                </div>
                <div className="quiz-summary-card success">
                  <span>Correct</span>
                  <strong>{statsCorrect}</strong>
                </div>
                <div className="quiz-summary-card danger">
                  <span>Wrong</span>
                  <strong>{statsWrong}</strong>
                </div>
                <div className="quiz-summary-card warning">
                  <span>Accuracy</span>
                  <strong>{`${statsAccuracy.toFixed(0)}%`}</strong>
                </div>
                <div className="quiz-summary-card neutral">
                  <span>Time Taken</span>
                  <strong>{formatDuration(statsSource?.timeTakenSeconds || 0)}</strong>
                </div>
              </div>
              {latestAttemptSummary?.attemptNumber ? (
                <div className="quiz-preview-note">
                  Latest attempt recorded as attempt #{latestAttemptSummary.attemptNumber}.
                </div>
              ) : null}
              <div className="quiz-performance-bars" aria-label="Overall result breakdown">
                <div className="quiz-bar-row">
                  <div className="quiz-bar-label">Correct</div>
                  <div className="quiz-bar-track">
                    <span
                      className="quiz-bar-fill correct"
                      style={{ width: `${statsTotalMarks ? (statsCorrect / statsTotalMarks) * 100 : 0}%` }}
                    />
                  </div>
                  <div className="quiz-bar-value">{statsCorrect}</div>
                </div>
                <div className="quiz-bar-row">
                  <div className="quiz-bar-label">Wrong</div>
                  <div className="quiz-bar-track">
                    <span
                      className="quiz-bar-fill wrong"
                      style={{ width: `${statsTotalMarks ? (statsWrong / statsTotalMarks) * 100 : 0}%` }}
                    />
                  </div>
                  <div className="quiz-bar-value">{statsWrong}</div>
                </div>
                <div className="quiz-bar-row">
                  <div className="quiz-bar-label">Unattempted</div>
                  <div className="quiz-bar-track">
                    <span
                      className="quiz-bar-fill skipped"
                      style={{
                        width: `${statsTotalMarks ? ((statsTotalMarks - statsAttempted) / statsTotalMarks) * 100 : 0}%`,
                      }}
                    />
                  </div>
                  <div className="quiz-bar-value">{Math.max(statsTotalMarks - statsAttempted, 0)}</div>
                </div>
              </div>
              <div className="quiz-difficulty-chart">
                <div className="quiz-difficulty-chart-head">
                  <div>
                    <h3>Difficulty Analysis</h3>
                    <p>Click any question number to open that question in the solution panel.</p>
                  </div>
                </div>
                {["easy", "medium", "hard"].map((level) => {
                  const bucket = difficultyStats[level];
                  const metrics = [
                    {
                      key: "attempted",
                      label: "Attempted",
                      value: bucket.attempted,
                      className: "attempted",
                    },
                    {
                      key: "correct",
                      label: "Correct",
                      value: bucket.correct,
                      className: "correct",
                    },
                    {
                      key: "wrong",
                      label: "Wrong",
                      value: bucket.wrong,
                      className: "wrong",
                    },
                    {
                      key: "unattempted",
                      label: "Unattempted",
                      value: bucket.unattempted,
                      className: "skipped",
                    },
                  ];
                  return (
                    <div className="quiz-difficulty-card" key={`diff-${level}`}>
                      <div className="quiz-difficulty-card-head">
                        <div>
                          <div className="quiz-difficulty-label">{level.toUpperCase()}</div>
                          <div className="quiz-difficulty-values">
                            <span>{`${bucket.total} questions`}</span>
                            <span>{`${bucket.correct} correct • ${bucket.wrong} wrong`}</span>
                          </div>
                        </div>
                        <div className="quiz-difficulty-pill-row">
                          <span className="quiz-difficulty-pill success">{`Correct ${bucket.correct}`}</span>
                          <span className="quiz-difficulty-pill danger">{`Wrong ${bucket.wrong}`}</span>
                        </div>
                      </div>
                      <div className="quiz-difficulty-metrics">
                        {metrics.map((metric) => (
                          <div className="quiz-difficulty-metric" key={`${level}-${metric.key}`}>
                            <div className="quiz-difficulty-metric-row">
                              <span>{metric.label}</span>
                              <strong>{metric.value}</strong>
                            </div>
                            <div className="quiz-diff-track">
                              <span
                                className={`quiz-diff-fill ${metric.className}`}
                                style={{ width: `${bucket.total ? (metric.value / bucket.total) * 100 : 0}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="quiz-difficulty-question-grid">
                        {bucket.questions.map((question) => (
                          <button
                            key={`${level}-question-${question.number}`}
                            type="button"
                            className={`quiz-difficulty-question ${question.status}`}
                            onClick={() => openSolutionAtQuestion(question.index)}
                            disabled={!canOpenSolution}
                          >
                            {question.number}
                          </button>
                        ))}
                        {!bucket.questions.length ? (
                          <span className="quiz-difficulty-empty">No questions in this section.</span>
                        ) : null}
                      </div>
                      <div className="quiz-difficulty-legend">
                        <span><i className="quiz-legend-dot correct" />Correct</span>
                        <span><i className="quiz-legend-dot wrong" />Wrong</span>
                        <span><i className="quiz-legend-dot unattempted" />Unattempted</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <aside className="quiz-list-card quiz-leaderboard-sidebar" aria-label="Leaderboard">
              <div className="quiz-leaderboard-sidebar-head">
                <div>
                  <h2>Leaderboard</h2>
                  <p>Top performers for this quiz</p>
                </div>
                <span className="quiz-leaderboard-count">{leaderboardDisplayEntries.length}</span>
              </div>
              {hasPreviewLeaderboardEntries ? (
                <div className="quiz-preview-note">
                  Preview rankings are shown until more verified quiz attempts are recorded.
                </div>
              ) : null}
              <div className="quiz-leaderboard-sidebar-list">
                {leaderboardDisplayEntries.map((entry, index) => (
                  <div
                    key={`${entry.name}-${entry.rank}-${index}`}
                    className={`quiz-leaderboard-profile-card ${entry.isCurrentUser ? "current-user" : ""}`}
                  >
                    <div className="quiz-leaderboard-rank">{`#${entry.rank || index + 1}`}</div>
                    <div className="quiz-leaderboard-avatar" aria-hidden="true">
                      {getInitials(entry.name)}
                    </div>
                      <div className="quiz-leaderboard-profile-body">
                      <div className="quiz-leaderboard-profile-name-row">
                        <span className="quiz-leaderboard-profile-name">{entry.name}</span>
                        {entry.isCurrentUser ? <span className="quiz-you-badge">You</span> : null}
                      </div>
                      <div className="quiz-leaderboard-profile-meta">
                        <span>{`Score ${entry.score} / ${entry.totalMarks}`}</span>
                        <span>{`Time ${formatDuration(entry.timeTakenSeconds)}`}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </aside>
          </div>
          {isAuthenticated() ? (
            <div className="quiz-list-card quiz-content-section">
              <h2>My Attempts</h2>
              {myAttempts.length ? (
                <div className="quiz-faq-list">
                  {myAttempts.map((attempt, index) => {
                    const score = Number(attempt?.score) || 0;
                    const totalMarks = Number(attempt?.totalMarks) || 0;
                    const accuracy = clampPercent(
                      attempt?.accuracy ??
                        ((Number(attempt?.attemptedQuestions) || 0) > 0
                          ? ((Number(attempt?.correctAnswers) || 0) /
                              Number(attempt?.attemptedQuestions || 1)) *
                            100
                          : 0)
                    );
                    return (
                      <section key={`${attempt?.attemptNumber || "attempt"}-${index}`} className="quiz-faq-item">
                        <h3>{`Attempt #${attempt?.attemptNumber || myAttempts.length - index}`}</h3>
                        <p>
                          {`Score ${score} / ${totalMarks} | Accuracy ${accuracy.toFixed(0)}% | Correct ${
                            Number(attempt?.correctAnswers) || 0
                          } | Wrong ${Number(attempt?.wrongAnswers) || 0} | Time ${formatDuration(
                            attempt?.timeTakenSeconds || 0
                          )} | Submitted ${formatSubmittedAt(attempt?.submittedAt || attempt?.createdAt)}`}
                        </p>
                      </section>
                    );
                  })}
                </div>
              ) : (
                <p className="quiz-meta">No attempts recorded yet for this quiz.</p>
              )}
            </div>
          ) : null}
        </>
      )}
      {isSolutionOpen ? (
        <div className="quiz-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="topic-solution-title">
          <div className="quiz-confirm-card quiz-solution-card">
            <p className="quiz-modal-eyebrow">Attempt Solution</p>
            <h2 id="topic-solution-title">{title}</h2>
            <div className="question-lang-switch" role="group" aria-label="Solution language switch">
              <button
                type="button"
                className={`question-lang-pill ${solutionLanguage === "hi" ? "active" : ""}`}
                onClick={() => setSolutionLanguage("hi")}
                aria-pressed={solutionLanguage === "hi"}
              >
                HI
              </button>
              <button
                type="button"
                className={`question-lang-pill ${solutionLanguage === "en" ? "active" : ""}`}
                onClick={() => setSolutionLanguage("en")}
                aria-pressed={solutionLanguage === "en"}
              >
                EN
              </button>
            </div>
            {solutionLanguage === "hi" && !isSolutionHindiReady ? (
              <p className="translation-note">{solutionTranslationStatus || "Translating solutions..."}</p>
            ) : null}
            <div className="quiz-solution-layout">
              <div className="quiz-solution-main">
                {activeSolutionQuestion ? (
                  <div className="quiz-solution-item">
                    <h3>{`Q${solutionIndex + 1}. ${activeSolutionQuestion?.question || "Question"}`}</h3>
                    <div className="quiz-options">
                      {(activeSolutionQuestion?.options || []).map((optionText, optionIndex) => {
                        const selected = solutionPayload?.answers?.[solutionIndex]?.selectedOption || "";
                        const correct = canonicalSolutionQuestion?.correct_option || "";
                        const canonicalOption = canonicalSolutionQuestion?.options?.[optionIndex] || optionText;
                        const isCorrect = areOptionsEqual(canonicalOption, correct);
                        const isSelected = areOptionsEqual(canonicalOption, selected);
                        const optionClass = isCorrect
                          ? "quiz-option correct"
                          : isSelected
                          ? "quiz-option wrong"
                          : "quiz-option";
                        return (
                          <button
                            key={`sol-opt-${solutionIndex}-${optionIndex}`}
                            type="button"
                            className={optionClass}
                            disabled
                          >
                            {optionText}
                            {isSelected && isCorrect ? <span className="quiz-option-note right">You got it right</span> : null}
                            {isSelected && !isCorrect ? <span className="quiz-option-note wrong">Your answer</span> : null}
                            {!isSelected && isCorrect ? <span className="quiz-option-note right">Correct answer</span> : null}
                          </button>
                        );
                      })}
                    </div>
                    <div className="quiz-explanation">
                      <strong>Explanation:</strong>
                      <ExplanationContent
                        text={getExplanationPayload(activeSolutionQuestion).text || "No explanation available."}
                        diagramType={getExplanationPayload(activeSolutionQuestion).diagramType}
                        diagramData={getExplanationPayload(activeSolutionQuestion).diagramData}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
              <aside className="quiz-solution-nav" aria-label="Question number panel">
                <div className="quiz-solution-nav-title">Questions</div>
                <div className="quiz-solution-nav-grid">
                  {solutionQuestions.map((question, idx) => {
                    const selected = solutionPayload?.answers?.[idx]?.selectedOption || "";
                    const correct = question?.correct_option || "";
                    const statusClass = !selected
                      ? "unattempted"
                      : areOptionsEqual(selected, correct)
                      ? "correct"
                      : "wrong";
                    return (
                      <button
                        key={`sol-nav-${idx}`}
                        type="button"
                        className={`quiz-solution-index ${statusClass} ${idx === solutionIndex ? "active" : ""}`}
                        onClick={() => setSolutionIndex(idx)}
                      >
                        {idx + 1}
                      </button>
                    );
                  })}
                </div>
              </aside>
            </div>
            <div className="quiz-actions">
              <button
                type="button"
                className="quiz-secondary-btn"
                onClick={() => setSolutionIndex((prev) => Math.max(prev - 1, 0))}
                disabled={solutionIndex === 0}
              >
                Previous
              </button>
              <button
                type="button"
                className="quiz-start-btn"
                onClick={() => setSolutionIndex((prev) => Math.min(prev + 1, totalSolutionQuestions - 1))}
                disabled={solutionIndex >= totalSolutionQuestions - 1}
              >
                Next
              </button>
              <button type="button" className="quiz-secondary-btn" onClick={() => setIsSolutionOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}




